import React, { useState, useRef, useEffect } from 'react';
import { Plus, Search, FileText, Check, X, Upload, Loader2, Pencil, Calendar, Download, User as UserIcon, FileSpreadsheet, Trash2, RefreshCw, ChevronDown, ChevronUp, MoreHorizontal, MessageSquare, ImagePlus, Paperclip } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import Pagination from '../components/Pagination';
import ConfirmDialog from '../components/ConfirmDialog';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { dateToTimestamp, formatDateDisplay, normalizeDateForStorage, todayAsStorageDate } from '../utils/date';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MANUAL_SERVICE_OPTIONS = ['Inspección', 'Pase de corriente', 'Abasto de combustible', 'Cambio de llanta', 'Cerrajería', 'Grúa'];
const INSURER_OPTIONS = ['ASSA', 'Óptima de Seguros', 'La Regional de Seguros', 'Seguros Fedpa', 'Seguros Global'];
const invoiceRequestsInFlight = new Map();

const requestInvoices = (params) => {
  const key = JSON.stringify(params);
  const existingRequest = invoiceRequestsInFlight.get(key);
  if (existingRequest) return existingRequest;

  const request = axios.get(`${API_URL}/api/invoices`, { params });
  const trackedRequest = request.finally(() => {
    if (invoiceRequestsInFlight.get(key) === trackedRequest) {
      invoiceRequestsInFlight.delete(key);
    }
  });
  invoiceRequestsInFlight.set(key, trackedRequest);
  return trackedRequest;
};

const OBJECTION_CHARGE_TYPES = {
  night_guard: {
    label: 'Guardia nocturna',
    fixedAmount: 2,
    requiresPhoto: false
  },
  mileage: {
    label: 'Kilometraje',
    requiresPhoto: true
  },
  toll: {
    label: 'Peaje',
    requiresPhoto: true
  },
  price_modification: {
    label: 'Modificación de precio',
    requiresPhoto: false
  }
};

const RECLAIM_STATUS_FILTERS = [
  { value: 'reclamado', label: 'Pendiente' },
  { value: 'aprobado', label: 'Aprobado' },
  { value: 'rechazado', label: 'Rechazado' }
];

const formatObjectionChargeType = (type) => OBJECTION_CHARGE_TYPES[type]?.label || type || 'Sin motivo';
const formatReclaimStatusFilter = (status) =>
  RECLAIM_STATUS_FILTERS.find((item) => item.value === status)?.label || status;

const decodeUnicodeLiterals = (value = '') =>
  String(value).replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );

const normalizeInsurerName = (value = '') => {
  const insurer = decodeUnicodeLiterals(value).trim();
  if (!insurer) return '';

  const normalized = insurer.toLowerCase();

  if (normalized.includes('optima de seguros') || normalized.includes('óptima de seguros')) {
    return 'Óptima de Seguros';
  }

  if (normalized.includes('la regional de seguros')) {
    return 'La Regional de Seguros';
  }

  if (normalized.includes('seguros fedpa')) {
    return 'Seguros Fedpa';
  }

  if (normalized.includes('seguros global') || normalized.includes('global')) {
    return 'Seguros Global';
  }

  if (normalized.includes('assa')) {
    return 'ASSA';
  }

  return insurer;
};

const normalizeInvoice = (invoice) => ({
  ...invoice,
  insurer: normalizeInsurerName(invoice?.insurer),
  date: normalizeDateForStorage(invoice?.date),
  claimDate: normalizeDateForStorage(invoice?.claimDate),
  responseDate: normalizeDateForStorage(invoice?.responseDate),
  claimTechnicianCommentDate: normalizeDateForStorage(invoice?.claimTechnicianCommentDate),
  claimAdminCommentDate: normalizeDateForStorage(invoice?.claimAdminCommentDate),
  objectionCharges: Array.isArray(invoice?.objectionCharges) ? invoice.objectionCharges : [],
  paymentDate: normalizeDateForStorage(invoice?.paymentDate)
});

const normalizeMatchText = (value = '') =>
  decodeUnicodeLiterals(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const buildUserMatchCandidates = (user) => {
  const aliases = Array.isArray(user?.aliases) ? user.aliases : [];
  return [user?.name, ...(aliases || [])]
    .map((value) => normalizeMatchText(value))
    .filter(Boolean);
};

const getSourceMappingCandidates = (user, sourceKey) => {
  const mappings = user?.sourceMappings || {};
  const sourceValues = Array.isArray(mappings?.[sourceKey]) ? mappings[sourceKey] : [];
  return sourceValues
    .map((value) => normalizeMatchText(value))
    .filter(Boolean);
};

const findMatchingUser = (users, technicianName, sourceKey = '') => {
  const normalizedTechnician = normalizeMatchText(technicianName);
  if (!normalizedTechnician) return null;

  if (sourceKey) {
    const sourceMatch = users.find((user) =>
      getSourceMappingCandidates(user, sourceKey).some(
        (candidate) =>
          candidate === normalizedTechnician ||
          candidate.includes(normalizedTechnician) ||
          normalizedTechnician.includes(candidate)
      )
    );
    if (sourceMatch) return sourceMatch;
  }

  const exactMatch = users.find((user) =>
    buildUserMatchCandidates(user).some((candidate) => candidate === normalizedTechnician)
  );
  if (exactMatch) return exactMatch;

  return users.find((user) =>
    buildUserMatchCandidates(user).some(
      (candidate) =>
        candidate.includes(normalizedTechnician) || normalizedTechnician.includes(candidate)
    )
  ) || null;
};

const normalizeDuplicateSegment = (value = '') =>
  decodeUnicodeLiterals(String(value ?? ''))
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const getInvoiceDuplicateDisplay = (invoice = {}) => {
  const expediente = String(invoice?.expediente || invoice?.id || '').trim() || 'Sin expediente';
  const insurer = String(invoice?.insurer || '').trim() || 'Sin aseguradora';
  const service = String(invoice?.service || '').trim() || 'Sin servicio';
  const technician = String(invoice?.technician || invoice?.userName || '').trim() || 'Sin tecnico';
  return `${expediente} | ${insurer} | ${service} | ${technician}`;
};

const buildDuplicateExportRows = (duplicates = []) =>
  duplicates.map((item, index) => {
    const invoice = item?.invoice || {};
    return {
      '#': index + 1,
      Motivo: item?.reason === 'existing' ? 'Ya existe en el sistema' : 'Repetido en el archivo',
      Expediente: invoice?.expediente || invoice?.id || '',
      Fecha: formatDateDisplay(invoice?.date),
      Aseguradora: invoice?.insurer || '',
      Servicio: invoice?.service || '',
      Tecnico: invoice?.technician || invoice?.userName || '',
      Monto: Number(invoice?.amount) || 0,
      Recargo: Number(invoice?.surcharge) || 0,
      Total: (Number(invoice?.amount) || 0) + (Number(invoice?.surcharge) || 0),
      'Estatus externo': invoice?.externalStatus || '',
      Cliente: invoice?.clientName || '',
      Proveedor: invoice?.providerName || '',
      Placa: invoice?.vehiclePlate || '',
      Clave: buildInvoiceDuplicateKey(invoice),
    };
  });

  const buildInvoiceDuplicateKey = (invoice) => {
    const expediente = normalizeDuplicateSegment(invoice?.expediente || invoice?.id);
    return expediente || '';
  };

  const buildImportSummary = ({
    totalValidRecords,
    importedCount,
    skippedCount,
  }) => {
    if (totalValidRecords <= 0) {
      return {
        title: 'Importación sin resultados',
        message: 'No se importó ningún expediente porque el archivo no trae filas válidas.',
        type: 'warning',
      };
    }

    if (importedCount <= 0) {
      return {
        title: 'Importación detenida',
        message: `No se importó ningún expediente. Todos ya existen o vienen repetidos (${skippedCount}).`,
        type: 'warning',
      };
    }

    if (skippedCount > 0) {
      return {
        title: 'Importación parcial',
        message: `Se procesaron ${totalValidRecords} registros. Se importaron ${importedCount} expediente${importedCount > 1 ? 's' : ''} y se omitieron ${skippedCount} duplicado${skippedCount > 1 ? 's' : ''}.`,
        type: 'warning',
      };
    }

    return {
      title: 'Importación exitosa',
      message: `Se procesaron ${totalValidRecords} registros. Se importaron ${importedCount} expediente${importedCount > 1 ? 's' : ''} correctamente.`,
      type: 'success',
    };
  };

const isPendingForBilling = (invoice) =>
  invoice?.status === 'pendiente' && (invoice?.paymentStatus || 'pending') !== 'paid';

const isBilledInvoice = (invoice) =>
  invoice?.status === 'pendiente' && (invoice?.paymentStatus || 'pending') === 'paid';

const Invoices = () => {
  const { isManager, users, currentUser } = useAuth();
  const { addNotification } = useNotification();
  const activeUsers = users.filter((user) => user.active !== false);
  const canManageInvoices = isManager;

  const getInvoiceTechnicianAmount = (invoice) => {
    const amount = Number(invoice?.amount) || 0;
    const surcharge = Number(invoice?.surcharge) || 0;
    const user = activeUsers.find((item) => item.id === invoice?.userId);
    const insurer = normalizeInsurerName(invoice?.insurer);
    const insurerPercentages = user?.insurerPercentages || {};
    const percentage = Number(insurerPercentages?.[insurer]) || 100;

    return Number(((amount * (percentage / 100)) + surcharge).toFixed(2));
  };

  const importedCasesTabName = canManageInvoices ? 'Casos importados' : 'Casos Atendidos';
  const attendedCasesTabName = 'Casos atendidos';

  const [activeTab, setActiveTab] = useState(importedCasesTabName);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [startDate, setStartDate] = useState(() => `${todayAsStorageDate().slice(0, 7)}-01`);
  const [endDate, setEndDate] = useState(() => todayAsStorageDate());
  const [filterInsurers, setFilterInsurers] = useState([]);
  const [filterService, setFilterService] = useState('');
  const [filterTechnician, setFilterTechnician] = useState('');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('');
  const [filterExternalStatus, setFilterExternalStatus] = useState('');
  const [filterReclaimStatus, setFilterReclaimStatus] = useState('');
  const [filterObjectionChargeType, setFilterObjectionChargeType] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [serverTotal, setServerTotal] = useState(0);
  const [serverSummary, setServerSummary] = useState({ amount: 0, surcharge: 0 });
  const [serverTechnicianGroups, setServerTechnicianGroups] = useState([]);
  const [serverFacets, setServerFacets] = useState({ insurers: [], services: [], technicians: [] });

  // Selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkTechnicianId, setBulkTechnicianId] = useState('');
  const [bulkDateValue, setBulkDateValue] = useState('');

  // Modal states
  const [isObjectionModalOpen, setIsObjectionModalOpen] = useState(false);
  const [isBulkDateModalOpen, setIsBulkDateModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [objectionAmount, setObjectionAmount] = useState('');
  const [objectionSurcharge, setObjectionSurcharge] = useState('');
  const [objectionReason, setObjectionReason] = useState('');
  const [objectionChargeType, setObjectionChargeType] = useState('');
  const [objectionKilometers, setObjectionKilometers] = useState('');
  const [objectionKmPrice, setObjectionKmPrice] = useState('');
  const [objectionTollPrice, setObjectionTollPrice] = useState('');
  const [objectionInformationalPrice, setObjectionInformationalPrice] = useState('');
  const [objectionAttachmentFile, setObjectionAttachmentFile] = useState(null);
  const [pendingObjectionCharges, setPendingObjectionCharges] = useState([]);
  const [isUploadingObjectionAttachment, setIsUploadingObjectionAttachment] = useState(false);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [selectedCommentInvoice, setSelectedCommentInvoice] = useState(null);
  const [userCommentDraft, setUserCommentDraft] = useState('');
  const [adminCommentDraft, setAdminCommentDraft] = useState('');
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Confirmar',
    confirmClassName: 'bg-red-600 hover:bg-red-700',
    loading: false,
    action: null
  });

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadInsurer, setUploadInsurer] = useState('ASSA');
  const [isProcessing, setIsProcessing] = useState(false);
  const [importFeedback, setImportFeedback] = useState(null);
  const fileInputRef = useRef(null);
  const objectionAttachmentInputRef = useRef(null);
  const invoiceRequestIdRef = useRef(0);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [manualInvoice, setManualInvoice] = useState({
    date: todayAsStorageDate(),
    id: '',
    technicianId: '',
    technicianName: '',
    insurer: 'ASSA',
    service: '',
    amount: '',
    surcharge: '',
    totalAmount: '',
    authorizedAmount: ''
  });

  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Invoices state - fetched from MongoDB
  const [invoices, setInvoices] = useState([]);

  // Loans state
  const [loans, setLoans] = useState([]);

  // Fetch invoices from backend
  const fetchInvoices = async (pageOverride = currentPage) => {
    const requestId = invoiceRequestIdRef.current + 1;
    invoiceRequestIdRef.current = requestId;
    setLoading(true);
    setLoadError('');
    try {
      const normalizedTab = activeTab.toLowerCase();
      const view = normalizedTab === 'casos importados'
        ? 'imported'
        : normalizedTab === 'casos atendidos'
          ? 'attended'
          : normalizedTab === 'casos reclamados'
            ? 'reclaimed'
            : normalizedTab === 'casos a facturar'
              ? 'billable'
              : normalizedTab === 'casos facturados'
                ? 'billed'
                : '';
      const res = await requestInvoices({
        view,
        start_date: startDate,
        end_date: endDate,
        page: pageOverride,
        page_size: itemsPerPage,
        search: debouncedSearch,
        insurers: filterInsurers.join('|'),
        service: filterService,
        technician: filterTechnician,
        payment_status: filterPaymentStatus,
        external_status: filterExternalStatus,
        reclaim_status: filterReclaimStatus,
        objection_type: filterObjectionChargeType,
        user_id: canManageInvoices ? '' : (currentUser?.id || ''),
        user_name: canManageInvoices ? '' : (currentUser?.name || ''),
      });
      if (requestId === invoiceRequestIdRef.current) {
        const payload = res.data || {};
        setInvoices((payload.items || []).map(normalizeInvoice));
        setServerTotal(payload.total || 0);
        setServerSummary(payload.summary || { amount: 0, surcharge: 0 });
        setServerTechnicianGroups(payload.technicianGroups || []);
        setServerFacets(payload.facets || { insurers: [], services: [], technicians: [] });
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      if (requestId === invoiceRequestIdRef.current) {
        setLoadError('No fue posible cargar los expedientes. Verifica tu conexión e intenta nuevamente.');
      }
    } finally {
      if (requestId === invoiceRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const refreshAfterTransaction = async () => {
    setSelectedIds([]);
    setCurrentPage(1);
    await fetchInvoices(1);
  };

  // Fetch loans from backend
  const fetchLoans = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/loans`);
      setLoans(res.data.filter(loan => loan.status === 'pending'));
    } catch (error) {
      console.error('Error fetching loans:', error);
    }
  };

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchInvoices(), fetchLoans()]);
      addNotification('Datos Actualizados', 'La información se ha recargado correctamente', 'success');
    } catch (error) {
      console.error('Error refreshing data:', error);
      addNotification('Error', 'No se pudo actualizar la información', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchLoans();
  }, []);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => window.clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    const defaultStart = `${todayAsStorageDate().slice(0, 7)}-01`;
    const defaultEnd = todayAsStorageDate();
    if (activeTab === 'Casos a facturar' && startDate === defaultStart && endDate === defaultEnd) {
      // Pending cases do not expire: open this tab with the complete backlog.
      setStartDate('');
      setEndDate('');
    }
  }, [activeTab, startDate, endDate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, startDate, endDate, debouncedSearch, filterInsurers, filterService, filterTechnician, filterPaymentStatus, filterExternalStatus, filterReclaimStatus, filterObjectionChargeType]);

  useEffect(() => {
    fetchInvoices();
  }, [activeTab, startDate, endDate, currentPage, itemsPerPage, debouncedSearch, filterInsurers, filterService, filterTechnician, filterPaymentStatus, filterExternalStatus, filterReclaimStatus, filterObjectionChargeType, canManageInvoices, currentUser?.id, currentUser?.name]);

  useEffect(() => {
    if (!canManageInvoices && activeTab === 'Casos importados') {
      setActiveTab('Casos Atendidos');
    } else if (canManageInvoices && activeTab === 'Casos Atendidos') {
      setActiveTab('Casos importados');
    }
  }, [canManageInvoices, activeTab]);

  const tabs = [
    { name: importedCasesTabName, filter: canManageInvoices ? 'importado' : 'atendido' },
    ...(canManageInvoices ? [{ name: attendedCasesTabName, filter: 'atendido' }] : []),
    { name: 'Casos reclamados', filter: 'reclamado' },
    { name: 'Casos a facturar', filter: 'pendiente' },
    { name: 'Casos facturados', filter: 'paid' }
  ];

  const resetObjectionModalState = () => {
    setIsObjectionModalOpen(false);
    setSelectedInvoiceId(null);
    setObjectionReason('');
    setObjectionAmount('');
    setObjectionSurcharge('');
    setObjectionChargeType('');
    setObjectionKilometers('');
    setObjectionKmPrice('');
    setObjectionTollPrice('');
    setObjectionInformationalPrice('');
    setObjectionAttachmentFile(null);
    setPendingObjectionCharges([]);
    setIsUploadingObjectionAttachment(false);
    if (objectionAttachmentInputRef.current) {
      objectionAttachmentInputRef.current.value = '';
    }
  };

  // Business Logic - API calls
  const updateInvoiceAPI = async (id, data) => {
    try {
      await axios.put(`${API_URL}/api/invoices/${id}`, data);
      return true;
    } catch (error) {
      console.error('Error updating invoice:', error);
      if (error.response?.status === 409) {
        addNotification('Expediente duplicado', error.response.data?.detail || 'Ese expediente ya existe en Facturación.', 'warning');
      }
      return false;
    }
  };

  const createInvoiceAPI = async (data, allowDuplicate = false) => {
    try {
      const res = await axios.post(`${API_URL}/api/invoices`, data, {
        params: allowDuplicate ? { allow_duplicate: true } : undefined
      });
      return res.data;
    } catch (error) {
      console.error('Error creating invoice:', error);
      if (error.response?.status === 409) {
        addNotification('Expediente duplicado', error.response.data?.detail || 'El expediente ya existe en FacturaciÃ³n.', 'warning');
      }
      return null;
    }
  };

  const deleteInvoiceAPI = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/invoices/${id}`);
      return true;
    } catch (error) {
      console.error('Error deleting invoice:', error);
      return false;
    }
  };

  const saveObjectionChargesAPI = async (id, charges) => {
    try {
      const res = await axios.put(`${API_URL}/api/invoices/${id}/objection-charges`, charges);
      return res.data || [];
    } catch (error) {
      console.error('Error saving objection charges:', error);
      return null;
    }
  };

  const updateObjectionChargeType = (type) => {
    setObjectionChargeType(type);
    setObjectionKilometers('');
    setObjectionTollPrice('');
    setObjectionInformationalPrice('');
    setObjectionAttachmentFile(null);
    setObjectionReason('');

    if (objectionAttachmentInputRef.current) {
      objectionAttachmentInputRef.current.value = '';
    }

    if (type === 'night_guard') {
      const invoice = invoices.find((item) => item.id === selectedInvoiceId);
      setObjectionAmount(String(Number(invoice?.amount) || 0));
      setObjectionSurcharge('2.00');
      setObjectionKmPrice('');
    } else if (type === 'mileage') {
      const invoice = invoices.find((item) => item.id === selectedInvoiceId);
      const technician = activeUsers.find((user) => user.id === invoice?.userId)
        || findMatchingUser(activeUsers, invoice?.technician, invoice?.insurer);
      const insurer = normalizeInsurerName(invoice?.insurer);
      const configuredPrice = technician?.insurerKmPrices?.[insurer];

      setObjectionKmPrice(
        configuredPrice === null || configuredPrice === undefined || configuredPrice === ''
          ? ''
          : String(configuredPrice)
      );
      setObjectionSurcharge('0');
    } else if (type === 'price_modification') {
      const invoice = invoices.find((item) => item.id === selectedInvoiceId);
      setObjectionKmPrice('');
      setObjectionSurcharge(String(Number(invoice?.surcharge) || 0));
    } else {
      setObjectionKmPrice('');
      setObjectionSurcharge('0');
    }
  };

  const updateObjectionMileage = (field, value) => {
    const nextKilometers = field === 'kilometers' ? value : objectionKilometers;
    const nextPrice = field === 'price' ? value : objectionKmPrice;
    const amount = (Number(nextKilometers) || 0) * (Number(nextPrice) || 0);

    if (field === 'kilometers') {
      setObjectionKilometers(value);
    } else {
      setObjectionKmPrice(value);
    }

    setObjectionSurcharge(amount ? amount.toFixed(2) : '0');
  };

  const updateObjectionTollPrice = (value) => {
    setObjectionTollPrice(value);
    setObjectionSurcharge(value ? Number(value).toFixed(2) : '0');
  };

  const buildObjectionCharge = () => {
    const config = OBJECTION_CHARGE_TYPES[objectionChargeType];

    if (!config) {
      return null;
    }

    const baseCharge = {
      type: objectionChargeType,
      label: config.label,
      amount: Number(objectionSurcharge || 0),
      comment: objectionReason.trim(),
      requiresPhoto: Boolean(config.requiresPhoto)
    };

    if (objectionChargeType === 'mileage') {
      return {
        ...baseCharge,
        kilometers: Number(objectionKilometers || 0),
        pricePerKm: Number(objectionKmPrice || 0)
      };
    }

    if (objectionChargeType === 'toll') {
      return {
        ...baseCharge,
        tollPrice: Number(objectionTollPrice || 0)
      };
    }

    if (objectionChargeType === 'price_modification') {
      return {
        ...baseCharge,
        amount: 0,
        informationalPrice: Number(objectionInformationalPrice || 0)
      };
    }

    return baseCharge;
  };

  const validateObjectionDraft = () => {
    const config = OBJECTION_CHARGE_TYPES[objectionChargeType];
    if (!config) {
      addNotification('Motivo requerido', 'Selecciona el motivo del reclamo.', 'warning');
      return false;
    }
    if (objectionChargeType === 'mileage' && !Number(objectionKmPrice)) {
      addNotification('Tarifa no configurada', 'Define el precio por km para este técnico y aseguradora desde Usuarios.', 'warning');
      return false;
    }
    if (objectionChargeType === 'mileage' && !Number(objectionKilometers)) {
      addNotification('Kilometraje incompleto', 'Ingresa los kilómetros recorridos.', 'warning');
      return false;
    }
    if (objectionChargeType === 'toll' && !Number(objectionTollPrice)) {
      addNotification('Peaje incompleto', 'Ingresa el precio obligatorio de la caseta.', 'warning');
      return false;
    }
    if (objectionChargeType === 'price_modification' && !Number(objectionInformationalPrice)) {
      addNotification('Precio requerido', 'Ingresa el precio de la modificación.', 'warning');
      return false;
    }
    if (config.requiresPhoto && !objectionAttachmentFile) {
      addNotification('Foto requerida', 'Este motivo requiere adjuntar una foto.', 'warning');
      return false;
    }
    return true;
  };

  const clearObjectionDraft = () => {
    setObjectionChargeType('');
    setObjectionReason('');
    setObjectionSurcharge('0');
    setObjectionKilometers('');
    setObjectionKmPrice('');
    setObjectionTollPrice('');
    setObjectionInformationalPrice('');
    setObjectionAttachmentFile(null);
    if (objectionAttachmentInputRef.current) objectionAttachmentInputRef.current.value = '';
  };

  const handleAddObjectionCharge = () => {
    if (!validateObjectionDraft()) return;
    setPendingObjectionCharges((current) => [
      ...current,
      {
        clientId: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
        charge: buildObjectionCharge(),
        file: objectionAttachmentFile
      }
    ]);
    clearObjectionDraft();
  };

  const downloadDuplicateReport = (duplicates = []) => {
    if (!duplicates.length) return;

    const rows = buildDuplicateExportRows(duplicates);
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Duplicados');
    XLSX.writeFile(wb, `Duplicados_importacion_${todayAsStorageDate()}.xlsx`);
  };

  const handleApprove = async (id) => {
    const invoice = invoices.find(inv => inv.id === id);
    if (!invoice) return;

    if (invoice.status === 'importado') {
      const updated = { ...invoice, status: 'atendido' };
      if (await updateInvoiceAPI(id, updated)) {
        await refreshAfterTransaction();
        addNotification("Caso Publicado", `El caso #${id} ahora es visible para el técnico.`, "success");
      }
    } else if (invoice.status === 'atendido') {
      const updated = { ...invoice, status: 'pendiente' };
      if (await updateInvoiceAPI(id, updated)) {
        await refreshAfterTransaction();
      }
    } else if (invoice.status === 'reclamado') {
      const updated = { ...invoice, status: 'aprobado' };
      const updatedOk = await updateInvoiceAPI(id, updated);
      if (!updatedOk) return;

      const approvedAmount = Number(invoice.amount) || 0;
      const approvedSurcharge = Number(invoice.surcharge) || 0;
      const billableCopy = {
        date: invoice.date,
        expediente: invoice.expediente || invoice.id,
        technician: invoice.technician,
        userId: invoice.userId,
        userName: invoice.userName,
        insurer: invoice.insurer,
        service: invoice.service,
        amount: approvedAmount,
        surcharge: approvedSurcharge,
        technicianAmount: approvedAmount + approvedSurcharge,
        originalAmount: invoice.originalAmount,
        totalAmount: approvedAmount + approvedSurcharge,
        authorizedAmount: approvedAmount,
        externalStatus: invoice.externalStatus || '',
        providerName: invoice.providerName || invoice.technician,
        clientName: invoice.clientName || '',
        companyName: invoice.companyName || '',
        vehiclePlate: invoice.vehiclePlate || '',
        sourceFormat: invoice.sourceFormat || 'manual',
        status: 'pendiente',
        claimDate: invoice.claimDate,
        observation: invoice.observation,
        networkObservation: `Reclamo Aprobado. Copia de ${invoice.id}`,
        responseDate: '',
        claimTechnicianComment: invoice.claimTechnicianComment || '',
        claimTechnicianCommentDate: invoice.claimTechnicianCommentDate || '',
        claimAdminComment: invoice.claimAdminComment || '',
        claimAdminCommentDate: invoice.claimAdminCommentDate || '',
        paymentStatus: 'pending',
        paymentDate: ''
      };
      const newInv = await createInvoiceAPI(billableCopy, true);
      if (newInv) {
        await refreshAfterTransaction();
      }
    }
  };

  const handleRejectClick = async (id) => {
    const invoice = invoices.find(inv => inv.id === id);
    if (!invoice) return;

    if (invoice.status === 'importado') {
      openConfirmDialog({
        title: 'Eliminar caso importado',
        message: 'Este caso importado se eliminará definitivamente.',
        confirmLabel: 'Eliminar',
        confirmClassName: 'bg-red-600 hover:bg-red-700',
        action: async () => {
          if (await deleteInvoiceAPI(id)) {
            setInvoices(prev => prev.filter(inv => inv.id !== id));
            addNotification("Caso Eliminado", `El caso #${id} ha sido eliminado.`, "info");
          }
          //Como solvento esto?
        }
      });
    } else if (invoice.status === 'atendido') {
      setSelectedInvoiceId(id);
      setObjectionAmount(invoice.amount.toString());
      setObjectionSurcharge(invoice.surcharge ? invoice.surcharge.toString() : '0');
      setObjectionReason('');
      setObjectionChargeType('');
      setObjectionKilometers('');
      setObjectionKmPrice('');
      setObjectionTollPrice('');
      setObjectionInformationalPrice('');
      setObjectionAttachmentFile(null);
      if (objectionAttachmentInputRef.current) {
        objectionAttachmentInputRef.current.value = '';
      }
      setIsObjectionModalOpen(true);
    } else if (invoice.status === 'reclamado') {
      const updated = { ...invoice, status: 'rechazado' };
      const updatedOk = await updateInvoiceAPI(id, updated);
      if (!updatedOk) return;

      const billableCopy = buildRejectedBillableCopy(invoice);
      const newInv = await createInvoiceAPI(billableCopy, true);
      if (newInv) {
        await refreshAfterTransaction();
      }
    }
  };

  const handleConfirmObjection = async () => {
    if (selectedInvoiceId) {
      const invoice = invoices.find(inv => inv.id === selectedInvoiceId);
      if (!invoice) return;

      let entries = [...pendingObjectionCharges];
      if (objectionChargeType) {
        if (!validateObjectionDraft()) return;
        entries.push({
          clientId: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
          charge: buildObjectionCharge(),
          file: objectionAttachmentFile
        });
      }
      if (!entries.length) {
        addNotification('Objeción requerida', 'Agrega al menos un motivo antes de enviar.', 'warning');
        return;
      }

      const today = todayAsStorageDate();
      const charges = entries.map((entry) => entry.charge);
      const comments = charges
        .filter((charge) => charge.comment)
        .map((charge) => `${charge.label}: ${charge.comment}`)
        .join('\n');
      const totalSurcharge = charges.reduce((sum, charge) => sum + (Number(charge.amount) || 0), 0);
      const priceModification = charges.find((charge) => charge.type === 'price_modification');
      const modifiedPrice = Number(priceModification?.informationalPrice);
      const claimAmount = Number.isFinite(modifiedPrice) && modifiedPrice > 0
        ? modifiedPrice
        : (Number(objectionAmount) || Number(invoice.amount) || 0);
      const updated = {
        ...invoice,
        status: 'reclamado',
        originalAmount: invoice.amount,
        amount: claimAmount,
        surcharge: totalSurcharge,
        totalAmount: claimAmount + totalSurcharge,
        authorizedAmount: claimAmount,
        observation: invoice.observation || '',
        claimDate: today,
        networkObservation: '',
        responseDate: '',
        claimTechnicianComment: comments,
        claimTechnicianCommentDate: today,
        claimAdminComment: '',
        claimAdminCommentDate: '',
        objectionCharges: charges,
        objectionAttachment: invoice.objectionAttachment || null
      };

      if (await updateInvoiceAPI(selectedInvoiceId, updated)) {
        const savedCharges = await saveObjectionChargesAPI(selectedInvoiceId, updated.objectionCharges);
        if (!savedCharges) {
          addNotification('Error', 'No fue posible guardar la tabla de motivos del reclamo.', 'error');
          return;
        }

        try {
          setIsUploadingObjectionAttachment(true);
          for (let index = 0; index < entries.length; index += 1) {
            const file = entries[index].file;
            const savedCharge = savedCharges[index];
            if (!file || !savedCharge?.id) continue;
            await uploadObjectionChargeAttachmentAPI(
              selectedInvoiceId,
              savedCharge.id,
              file
            );
          }
        } catch (error) {
          console.error('Error uploading objection attachment:', error);
          addNotification('Adjunto no cargado', 'El reclamo se guardó, pero no se pudieron subir todas las imágenes.', 'warning');
        } finally {
          setIsUploadingObjectionAttachment(false);
        }

        addNotification("Nuevo reclamo", `El caso #${selectedInvoiceId} ha sido objetado.`, "warning");
        await refreshAfterTransaction();
      }
      resetObjectionModalState();
    }
  };

  // Manual creation
  const handleCreateManualSubmit = async () => {
    if (!manualInvoice.id || !manualInvoice.service) {
      addNotification(
        'Campos requeridos',
        'Por favor complete el número de expediente y el servicio.',
        'warning'
      );
      return;
    }

    let technicianName = manualInvoice.technicianName || 'Desconocido';
    let odataUserId = null;
    let odataUserName = null;

    if (manualInvoice.technicianId) {
      const user = users.find(u => u.id === manualInvoice.technicianId);
      if (user) {
        odataUserId = user.id;
        odataUserName = user.name;
        technicianName = user.name;
      }
    }

    const newInvoiceData = {
      date: normalizeDateForStorage(manualInvoice.date),
      expediente: manualInvoice.id,
      technician: technicianName,
      userId: odataUserId,
      userName: odataUserName,
      insurer: manualInvoice.insurer,
      service: manualInvoice.service,
      amount: parseFloat(manualInvoice.amount) || 0,
      surcharge: parseFloat(manualInvoice.surcharge) || 0,
      technicianAmount: (parseFloat(manualInvoice.amount) || 0) + (parseFloat(manualInvoice.surcharge) || 0),
      originalAmount: parseFloat(manualInvoice.amount) || 0,
      totalAmount: parseFloat(manualInvoice.totalAmount) || 0,
      authorizedAmount: parseFloat(manualInvoice.authorizedAmount) || parseFloat(manualInvoice.amount) || 0,
      externalStatus: 'MANUAL',
      providerName: technicianName,
      clientName: '',
      companyName: '',
      vehiclePlate: '',
      sourceFormat: manualInvoice.insurer === 'Óptima de Seguros' ? 'manual-optima' : 'manual',
      status: 'importado',
      claimDate: '',
      observation: '',
      networkObservation: '',
      responseDate: '',
      claimTechnicianComment: '',
      claimTechnicianCommentDate: '',
      claimAdminComment: '',
      claimAdminCommentDate: '',
      paymentStatus: 'pending',
      paymentDate: ''
    };

    const created = await createInvoiceAPI(newInvoiceData);
    if (created) {
      setInvoices(prev => [normalizeInvoice(created), ...prev]);
      setIsCreateModalOpen(false);
      setManualInvoice({
        date: todayAsStorageDate(),
        id: '',
        technicianId: '',
        technicianName: '',
        insurer: 'ASSA',
        service: '',
        amount: '',
        surcharge: '',
        totalAmount: '',
        authorizedAmount: ''
      });
      addNotification("Expediente Creado", `Se ha creado el caso.`, "success");
    } else {
      addNotification('Error', 'Error al crear el expediente.', 'error');
    }
  };

  // Inline editing
  const startEditing = (invoice, field) => {
    const universallyEditable = ['date', 'expediente', 'technician', 'insurer', 'service'];
    if (['aprobado', 'rechazado'].includes(invoice.status) && !universallyEditable.includes(field) && field !== 'networkObservation') return;
    let isAllowed = false;
    if (['date', 'expediente', 'technician', 'insurer', 'service', 'networkObservation'].includes(field)) {
      isAllowed = canManageInvoices;
    } else {
      if (activeTab === importedCasesTabName) {
        isAllowed = canManageInvoices;
      } else {
        isAllowed = (
          activeTab === 'Casos a facturar'
          || activeTab === 'Casos reclamados'
          || activeTab === attendedCasesTabName
        ) && canManageInvoices;
      }
    }

    if (isAllowed) {
      setEditingCell({ id: invoice.id, field });
      if (field === 'technician') {
        setEditValue(invoice.userId || '');
      } else {
        setEditValue(invoice[field] ? invoice[field].toString() : '');
      }
    }
  };

  const saveEdit = async () => {
    if (editingCell) {
      const invoice = invoices.find(inv => inv.id === editingCell.id);
      if (!invoice) return;

      if (editingCell.field === 'technician') {
        const selectedUser = users.find(u => u.id === editValue);
        const updated = {
          ...invoice,
          userId: selectedUser ? selectedUser.id : null,
          userName: selectedUser ? selectedUser.name : null,
          technician: selectedUser ? selectedUser.name : 'Sin asignar'
        };
        if (await updateInvoiceAPI(editingCell.id, updated)) {
          setInvoices(prev => prev.map(inv => inv.id === editingCell.id ? normalizeInvoice(updated) : inv));
        }
        setEditingCell(null);
        setEditValue('');
        return;
      }

      const isNumeric = ['amount', 'surcharge', 'originalAmount', 'authorizedAmount', 'totalAmount'].includes(editingCell.field);
      let finalValue = editValue;

      if (['date', 'expediente', 'insurer', 'service'].includes(editingCell.field) && !String(editValue || '').trim()) {
        addNotification('Campo requerido', 'Este campo no puede quedar vacío.', 'warning');
        return;
      }

      if (isNumeric) {
        const parsed = parseFloat(editValue);
        if (isNaN(parsed)) return;
        finalValue = parsed;
      }

      const updated = { ...invoice, [editingCell.field]: finalValue };
      if (editingCell.field === 'amount') {
        if (activeTab === 'Casos reclamados') {
          updated.authorizedAmount = finalValue;
          updated.totalAmount = Number(finalValue) + (Number(invoice.surcharge) || 0);
        } else {
          updated.originalAmount = finalValue;
        }
      }

      if (await updateInvoiceAPI(editingCell.id, updated)) {
        setInvoices(prev => prev.map(inv => inv.id === editingCell.id ? normalizeInvoice(updated) : inv));
      }
      setEditingCell(null);
      setEditValue('');
    }
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    }
  };

  const formatCurrency = (amount) => {
    return (amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleTogglePaymentStatus = async (invoice) => {
    const nextStatus = (invoice.paymentStatus || 'pending') === 'paid' ? 'pending' : 'paid';
    const today = todayAsStorageDate();
    const updated = {
      ...invoice,
      paymentStatus: nextStatus,
      paymentDate: nextStatus === 'paid' ? today : ''
    };

    if (await updateInvoiceAPI(invoice.id, updated)) {
      await refreshAfterTransaction();
    }
  };

  const normalizeText = (value) => (value ?? '').toString().trim();

  const parseNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const cleaned = value.toString().replace(/[^0-9.,-]/g, '').replace(/,/g, '');
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatImportedDate = (value) => normalizeDateForStorage(value);

  const getImportedValue = (row, possibleNames, defaultValue = '') => {
    for (const name of possibleNames) {
      if (row[name] !== undefined && row[name] !== null && row[name] !== '') {
        return row[name];
      }
    }
    return defaultValue;
  };


const openConfirmDialog = (config) => {
  setConfirmState({
    open: true,
    title: config.title,
    message: config.message,
    confirmLabel: config.confirmLabel || 'Confirmar',
    cancelLabel: config.cancelLabel || 'Cancelar',
    confirmClassName: config.confirmClassName || 'bg-red-600 hover:bg-red-700',
    loading: false,
    action: config.action || null
  });
};

const closeConfirmDialog = () => {
  setConfirmState(prev => ({
    ...prev,
    open: false,
    loading: false,
    action: null
  }));
};

const runConfirmAction = async () => {
  setConfirmState(prev => ({ ...prev, loading: true }));
  try {
    if (confirmState.action) {
      await confirmState.action();
    }
  } finally {
    closeConfirmDialog();
  }
};

  const buildImportedInvoice = (row, sourceFormat) => {
    const expediente = normalizeText(getImportedValue(row, ['Expediente', 'expediente', 'Orden', 'orden', '# Expediente', 'N° Expediente', 'No. Expediente', 'Numero', 'Número de expediente'], ''));
    const fecha = getImportedValue(row, ['F.apertura', 'Fecha', 'fecha', 'F. apertura', 'Fecha Servicio', 'Fecha de apertura del expediente'], '');
    const tecnico = normalizeText(getImportedValue(row, ['Nombre Gruista', 'Gruista', 'gruista', 'Tecnico', 'Técnico', 'tecnico', 'técnico', 'Nombre Conductor Vehículo Servicio'], 'Sin asignar'));
    const servicio = normalizeText(getImportedValue(row, ['Avería', 'Averia', 'avería', 'averia', 'Servicio', 'servicio', 'Tipo de Servicio'], 'Servicio General'));
    const externalStatus = normalizeText(getImportedValue(row, ['Estado', 'Estado del Servicio'], ''));
    const subtotal = parseNumber(getImportedValue(row, ['SubTotal', 'Subtotal', 'subtotal', 'Monto', 'monto', 'Monto ASSA', 'Montos ASSA', 'Precio subtotal'], 0));
    const surcharge = parseNumber(getImportedValue(row, ['Costos', 'Costo', 'costos', 'costo', 'Recargo', 'recargo', 'Recargos'], 0));
    const totalAmount = parseNumber(getImportedValue(row, ['Total', 'Precio final'], subtotal + surcharge));
    const clientName = normalizeText(getImportedValue(row, ['Nombre Cliente', 'Nombre del Cliente'], ''));
    const companyName = normalizeText(getImportedValue(row, ['Nombre Compañía', 'Nombre Proveedor Servicio'], ''));
    const vehiclePlate = normalizeText(getImportedValue(row, ['Matrícula', 'Placa Vehículo Cliente', 'Placa Vehículo Servicio'], ''));

    if (!expediente || !servicio) {
      return null;
    }

    let userId = null;
    let userName = null;
    const matchedUser = findMatchingUser(users, tecnico, uploadInsurer);

    if (matchedUser) {
      userId = matchedUser.id;
      userName = matchedUser.name;
    }

    return {
      date: formatImportedDate(fecha),
      expediente,
      technician: tecnico || 'Sin asignar',
      userId,
      userName,
      insurer: uploadInsurer,
      service: servicio,
      amount: subtotal,
      surcharge,
      originalAmount: subtotal,
      totalAmount,
      authorizedAmount: totalAmount || subtotal,
      status: 'importado',
      externalStatus,
      providerName: tecnico || 'Sin asignar',
      clientName,
      companyName,
      vehiclePlate,
      sourceFormat,
      claimDate: '',
      observation: '',
      networkObservation: '',
      responseDate: '',
      claimTechnicianComment: '',
      claimTechnicianCommentDate: '',
      claimAdminComment: '',
      claimAdminCommentDate: '',
      paymentStatus: 'pending',
      paymentDate: ''
    };
  };

  // File processing
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleProcessFileLegacyUnused = async () => {
    if (!selectedFile) return;
    setIsProcessing(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array', cellDates: true });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Use sheet_to_json to get objects with column names as keys
          const rows = XLSX.utils.sheet_to_json(worksheet);

          console.log('=== DATOS DEL EXCEL ===');
          console.log('Primera fila:', rows[0]);
          console.log('Columnas disponibles:', Object.keys(rows[0] || {}));

          // Helper to get value from row by multiple possible column names
          const getValue = (row, possibleNames, defaultValue = '') => {
            for (const name of possibleNames) {
              if (row[name] !== undefined && row[name] !== null) {
                return row[name];
              }
            }
            return defaultValue;
          };

          // Helper to format date
          const formatDate = (value) => {
            if (!value) return '';
            if (value instanceof Date) {
              const day = value.getDate().toString().padStart(2, '0');
              const month = (value.getMonth() + 1).toString().padStart(2, '0');
              const year = value.getFullYear();
              return `${day}/${month}/${year}`;
            }
            if (typeof value === 'number') {
              const date = new Date((value - 25569) * 86400 * 1000);
              const day = date.getDate().toString().padStart(2, '0');
              const month = (date.getMonth() + 1).toString().padStart(2, '0');
              const year = date.getFullYear();
              return `${day}/${month}/${year}`;
            }
            if (typeof value === 'string' && (value.includes('T') || value.includes('-'))) {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                const day = date.getDate().toString().padStart(2, '0');
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const year = date.getFullYear();
                return `${day}/${month}/${year}`;
              }
            }
            return value.toString();
          };

          // Process each row
          const newInvoices = [];
          for (const row of rows) {
            // Get values using possible column names
            const expediente = getValue(row, ['Expediente', 'expediente', 'Orden', 'orden', 'N° Expediente', 'No. Expediente', 'Numero', 'Número de expediente'], '');
            const fecha = getValue(row, ['F.apertura', 'Fecha', 'fecha', 'F. apertura', 'Fecha Servicio'], '');
            const tecnico = getValue(row, ['Nombre Gruista', 'Gruista', 'gruista', 'Tecnico', 'Técnico', 'tecnico', 'técnico'], 'Sin asignar');
            const servicio = getValue(row, ['Avería', 'Averia', 'avería', 'averia', 'Servicio', 'servicio'], 'Servicio General');
            const monto = getValue(row, ['SubTotal', 'Subtotal', 'subtotal', 'Monto', 'monto', 'Montos ASSA'], 0);
            const recargo = getValue(row, ['Costos', 'Costo', 'costos', 'costo', 'Recargo', 'recargo', 'Recargos'], 0);

            // Skip empty rows
            if (!expediente && !tecnico) continue;

            // Try to match technician with users
            const tecnicoStr = tecnico.toString();
            let odataUserId = null;
            let odataUserName = null;
            const matchedUser = findMatchingUser(users, tecnicoStr, uploadInsurer);
            if (matchedUser) {
              odataUserId = matchedUser.id;
              odataUserName = matchedUser.name;
            }

            newInvoices.push({
              date: formatDate(fecha),
              expediente: expediente.toString(),
              technician: tecnicoStr,
              userId: odataUserId,
              userName: odataUserName,
              insurer: uploadInsurer,
              service: servicio.toString(),
              amount: parseFloat(monto) || 0,
              surcharge: parseFloat(recargo) || 0,
              originalAmount: parseFloat(monto) || 0,
              status: 'importado',
              claimDate: '',
              observation: '',
              networkObservation: '',
              responseDate: '',
              claimTechnicianComment: '',
              claimTechnicianCommentDate: '',
              claimAdminComment: '',
              claimAdminCommentDate: ''
            });
          }

          console.log('=== EXPEDIENTES A IMPORTAR ===');
          console.log('Total:', newInvoices.length);
          if (newInvoices.length > 0) {
            console.log('Primer expediente:', newInvoices[0]);
          }

          if (newInvoices.length > 0) {
            // Save all invoices to database via bulk API
            try {
              const res = await axios.post(`${API_URL}/api/invoices/bulk`, newInvoices);
              if (res.data.invoices) {
              setInvoices(prev => [...(res.data.invoices || []).map(normalizeInvoice), ...prev]);
              }
              setIsUploadModalOpen(false);
              setSelectedFile(null);
              const summary = buildImportSummary({
                totalValidRecords: newInvoices.length,
                importedCount: res.data.created || 0,
                skippedCount: res.data.skipped || 0
              });
              addNotification(summary.title, summary.message, summary.type);
            } catch (apiError) {
              console.error("Error saving to database:", apiError);
              addNotification('Error', 'Error al guardar en la base de datos', 'error');
            }
          } else {
            addNotification(
              'Importación sin resultados',
              'No se encontraron datos válidos en el archivo.',
              'warning'
            );
          }
        } catch (parseError) {
          console.error("Error parsing Excel:", parseError);
          addNotification('Error', 'Error al leer el archivo. Verifique el formato.', 'error');
        }
        setIsProcessing(false);
      };
      reader.readAsArrayBuffer(selectedFile);
    } catch (error) {
      console.error("Error al procesar archivo", error);
      addNotification('Error', 'Hubo un error al procesar el archivo.', 'error');
      setIsProcessing(false);
    }
  };

  const handleProcessFile = async () => {
    if (!selectedFile) return;
    setIsProcessing(true);
    try {
      let rows = [];
      const isCsv = selectedFile.name.toLowerCase().endsWith('.csv');
      const sourceFormat = isCsv ? 'csv-assa' : 'xlsx-services';

      if (isCsv) {
        const text = await selectedFile.text();
        // ASSA exports dates as dd/mm/yyyy. Without raw mode SheetJS guesses
        // them as US mm/dd/yyyy (09/10 became September 10, for example).
        const workbook = XLSX.read(text, { type: 'string', FS: ';', raw: true });
        const firstSheetName = workbook.SheetNames[0];
        rows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], { defval: '' });
      } else {
        const data = await selectedFile.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const firstSheetName = workbook.SheetNames[0];
        rows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], { defval: '' });
      }

      const importedInvoices = rows
        .map((row) => buildImportedInvoice(row, sourceFormat))
        .filter(Boolean);

      if (importedInvoices.length === 0) {
        const message = 'No se importo ningun expediente porque el archivo no trae filas validas.';
        const summary = {
          title: 'Importacion sin resultados',
          message,
          type: 'warning'
        };
        setImportFeedback(summary);
        addNotification(summary.title, summary.message, summary.type);
        setSelectedFile(null);
        setIsUploadModalOpen(false);
        return;
      }

      const existingKeys = new Set(
        invoices
          .map((invoice) => buildInvoiceDuplicateKey(invoice))
          .filter(Boolean)
      );
      const fileKeys = new Set();
      const skippedDuplicates = [];

      const newInvoices = importedInvoices.filter((invoice) => {
        const duplicateKey = buildInvoiceDuplicateKey(invoice);
        if (!duplicateKey) return true;

        if (existingKeys.has(duplicateKey)) {
          skippedDuplicates.push({ invoice, reason: 'existing' });
          return false;
        }

        if (fileKeys.has(duplicateKey)) {
          skippedDuplicates.push({ invoice, reason: 'file' });
          return false;
        }

        fileKeys.add(duplicateKey);
        return true;
      });

      if (skippedDuplicates.length > 0) {
        const duplicateMessage = `Se omitieron ${skippedDuplicates.length} expediente${skippedDuplicates.length > 1 ? 's' : ''} duplicado${skippedDuplicates.length > 1 ? 's' : ''} antes de importar.`;
        const duplicatePreview = skippedDuplicates
          .slice(0, 5)
          .map((item) => getInvoiceDuplicateDisplay(item.invoice));
        const duplicateSummary = {
          title: 'Duplicados detectados',
          message: duplicateMessage,
          type: 'warning',
          duplicates: skippedDuplicates,
          duplicatePreview,
        };
        setImportFeedback(duplicateSummary);
        addNotification(duplicateSummary.title, duplicateSummary.message, duplicateSummary.type);
      }

      if (newInvoices.length === 0) {
        const summaryBase = buildImportSummary({
          totalValidRecords: importedInvoices.length,
          importedCount: 0,
          skippedCount: skippedDuplicates.length
        });
        const summary = {
          ...summaryBase,
          duplicates: skippedDuplicates,
          duplicatePreview: skippedDuplicates
            .slice(0, 5)
            .map((item) => getInvoiceDuplicateDisplay(item.invoice)),
        };
        setImportFeedback(summary);
        setSelectedFile(null);
        setIsUploadModalOpen(false);
        addNotification(summary.title, summary.message, summary.type);
        return;
      }

      const res = await axios.post(`${API_URL}/api/invoices/bulk`, newInvoices);
      if (res.data.invoices) {
        setInvoices(prev => [...(res.data.invoices || []).map(normalizeInvoice), ...prev]);
      }
      const summaryBase = buildImportSummary({
        totalValidRecords: importedInvoices.length,
        importedCount: res.data.created || 0,
        skippedCount: skippedDuplicates.length + (res.data.skipped || 0)
      });
      const serverDuplicates = (res.data.duplicates || []).map(item => ({
        invoice: { expediente: item.expediente },
        reason: item.reason
      }));
      const allDuplicates = [...skippedDuplicates, ...serverDuplicates];
      const summary = allDuplicates.length > 0
        ? {
            ...summaryBase,
            duplicates: allDuplicates,
            duplicatePreview: allDuplicates
              .slice(0, 5)
              .map((item) => getInvoiceDuplicateDisplay(item.invoice)),
          }
        : summaryBase;
      setImportFeedback(summary);
      setSelectedFile(null);
      setIsUploadModalOpen(false);
      addNotification(summary.title, summary.message, summary.type);
    } catch (error) {
      console.error("Error al procesar archivo", error);
      setImportFeedback({
        title: 'Error',
        message: 'Hubo un error al procesar el archivo.',
        type: 'error'
      });
      addNotification('Error', 'Hubo un error al procesar el archivo.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Filter invoices
  const hasManualFilters = [
    searchTerm,
    startDate,
    endDate,
    filterInsurers.length > 0,
    filterService,
    filterTechnician,
    filterPaymentStatus,
    filterExternalStatus,
    ...(activeTab === 'Casos reclamados' ? [filterReclaimStatus, filterObjectionChargeType] : [])
  ].some(Boolean);

  const shouldApplyRecentWindow =
    !hasManualFilters && (activeTab === 'Casos reclamados' || activeTab === 'Casos a facturar' || activeTab === 'Casos facturados');

  const recentWindowStart = (() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    now.setDate(now.getDate() - 15);
    return now.getTime();
  })();

  const invoiceBelongsToCurrentUser = (invoice) => {
    if (canManageInvoices) return true;
    if (!currentUser) return false;

    const currentName = (currentUser.name || '').trim().toLowerCase();
    const invoiceUserName = (invoice.userName || '').trim().toLowerCase();
    const invoiceTechnician = (invoice.technician || '').trim().toLowerCase();

    return (
      invoice.userId === currentUser.id ||
      (!!currentName && invoiceUserName === currentName) ||
      (!!currentName && invoiceTechnician === currentName)
    );
  };

  const filteredInvoices = invoices.filter(inv => {
    if (!invoiceBelongsToCurrentUser(inv)) return false;

    const matchesSearch =
      (inv.id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inv.expediente || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inv.technician || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inv.insurer || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inv.service || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inv.externalStatus || '').toLowerCase().includes(searchTerm.toLowerCase());

    let matchesDate = true;
    const invDateTs = dateToTimestamp(inv.date || inv.claimDate);

    if (startDate) {
      const [sy, sm, sd] = startDate.split('-').map(Number);
      const startTs = new Date(sy, sm - 1, sd).getTime();
      if (invDateTs < startTs) matchesDate = false;
    }
    if (endDate) {
      const [ey, em, ed] = endDate.split('-').map(Number);
      const endTs = new Date(ey, em - 1, ed, 23, 59, 59).getTime();
      if (invDateTs > endTs) matchesDate = false;
    }

    // Filter by insurer
    const matchesInsurer = filterInsurers.length > 0 ? filterInsurers.includes(inv.insurer) : true;

    // Filter by service
    const matchesService = filterService ? inv.service === filterService : true;

    // Filter by technician
    const matchesTechnician = filterTechnician ? inv.technician === filterTechnician : true;
    const matchesPaymentStatus = filterPaymentStatus ? (inv.paymentStatus || 'pending') === filterPaymentStatus : true;
    const matchesExternalStatus = filterExternalStatus ? (inv.externalStatus || '') === filterExternalStatus : true;
    const matchesRecentWindow = shouldApplyRecentWindow ? invDateTs >= recentWindowStart : true;
    const matchesReclaimStatus = filterReclaimStatus ? inv.status === filterReclaimStatus : true;
    const matchesObjectionChargeType = filterObjectionChargeType
      ? (inv.objectionCharges || []).some((charge) => charge?.type === filterObjectionChargeType)
      : true;

    if (activeTab === 'Casos reclamados') {
      const isReclaimStatus = ['reclamado', 'aprobado', 'rechazado'].includes(inv.status);
      return isReclaimStatus && matchesSearch && matchesDate && matchesInsurer && matchesService && matchesTechnician && matchesPaymentStatus && matchesExternalStatus && matchesReclaimStatus && matchesObjectionChargeType && matchesRecentWindow;
    }

      const matchesTab = activeTab === 'Casos a facturar'
        ? isPendingForBilling(inv)
        : activeTab === 'Casos facturados'
          ? isBilledInvoice(inv)
          : tabs.find(t => t.name === activeTab)?.filter === inv.status;
      return matchesTab && matchesSearch && matchesDate && matchesInsurer && matchesService && matchesTechnician && matchesPaymentStatus && matchesExternalStatus && matchesRecentWindow;
  });

  const sortedInvoices = [...filteredInvoices].sort(
    (a, b) => dateToTimestamp(b.date || b.claimDate) - dateToTimestamp(a.date || a.claimDate)
  );

  // The "Costo" column shows `amount`, so the subtotal must match that visible column.
  const totalCostAmount = Number(serverSummary.amount) || 0;
  const totalSurcharge = Number(serverSummary.surcharge) || 0;
  const totalTechnicianAmount = serverTechnicianGroups.reduce(
    (sum, group) => sum + getInvoiceTechnicianAmount(group),
    0
  );
  const isReclaimedTab = activeTab === 'Casos reclamados';
  const isBillingTab = activeTab === 'Casos a facturar';
  const isBilledTab = activeTab === 'Casos facturados';
  const showInvoiceActions = !isBilledTab;
  const attendedSelectionEnabled = canManageInvoices && activeTab === attendedCasesTabName;
  const isStandardEditable = (isBillingTab && canManageInvoices) || (activeTab === importedCasesTabName && canManageInvoices) || (isReclaimedTab && canManageInvoices);
  const isCostEditable = isStandardEditable || (activeTab === attendedCasesTabName && canManageInvoices);
  const importedSelectionEnabled = canManageInvoices && activeTab === importedCasesTabName;
  const reclaimedSelectionEnabled = canManageInvoices && activeTab === 'Casos reclamados';
  const billingSelectionEnabled = canManageInvoices && isBillingTab;
  const billedSelectionEnabled = canManageInvoices && isBilledTab;
  const bulkSelectionEnabled = importedSelectionEnabled || reclaimedSelectionEnabled || billingSelectionEnabled || billedSelectionEnabled || attendedSelectionEnabled;
  const technicianUsers = activeUsers.filter((user) => ['collaborator', 'colaborador'].includes(user.role));
  const isInvoiceBulkSelectable = (invoice) => {
    if (importedSelectionEnabled) return invoice.status === 'importado';
    if (attendedSelectionEnabled) return invoice.status === 'atendido';
    if (reclaimedSelectionEnabled) return invoice.status === 'reclamado';
    if (billingSelectionEnabled) return isPendingForBilling(invoice);
    if (billedSelectionEnabled) return isBilledInvoice(invoice);
    return false;
  };

  // The backend already returns only the visible page.
  const paginatedInvoices = sortedInvoices;
  const totalItems = serverTotal;
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderEditableField = (invoice, field, displayValue, { type = 'text', options = [] } = {}) => {
    const editing = editingCell?.id === invoice.id && editingCell?.field === field;
    if (editing) {
      if (options.length) {
        const availableOptions = [...new Set([invoice[field], ...options].filter(Boolean))];
        return (
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={saveEdit}
            onKeyDown={handleEditKeyDown}
            onClick={(e) => e.stopPropagation()}
            autoFocus
            className="w-full min-w-32 rounded-lg border border-primary bg-white px-2 py-1.5 text-sm focus:outline-none"
          >
            {availableOptions.map(option => <option key={option} value={option}>{option}</option>)}
          </select>
        );
      }
      return (
        <input
          type={type}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={saveEdit}
          onKeyDown={handleEditKeyDown}
          onClick={(e) => e.stopPropagation()}
          autoFocus
          className="w-full min-w-28 rounded-lg border border-primary bg-white px-2 py-1.5 text-sm focus:outline-none"
        />
      );
    }
    return (
      <button type="button" onClick={() => startEditing(invoice, field)} className="inline-flex max-w-full items-center gap-2 text-left text-slate-700">
        <span className="truncate">{displayValue || '—'}</span>
        {canManageInvoices && <Pencil size={12} className="shrink-0 text-slate-400" />}
      </button>
    );
  };
  const handleItemsPerPageChange = (size) => {
    setItemsPerPage(size);
    setCurrentPage(1);
  };

  // Get unique insurers and technicians for filters
  const uniqueInsurers = serverFacets.insurers || [];
  const uniqueServices = serverFacets.services || [];
  const uniqueTechnicians = serverFacets.technicians || [];
  const toggleInsurerFilter = (insurer) => {
    setFilterInsurers((current) =>
      current.includes(insurer)
        ? current.filter((item) => item !== insurer)
        : [...current, insurer]
    );
  };

  const uploadObjectionChargeAttachmentAPI = async (invoiceId, chargeId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(
      `${API_URL}/api/invoices/${invoiceId}/objection-charges/${chargeId}/attachment`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return res.data;
  };
  // Count active filters
  const activeFiltersCount = [
    searchTerm,
    startDate,
    endDate,
    filterInsurers.length > 0,
    filterService,
    filterTechnician,
    filterPaymentStatus,
    filterExternalStatus,
    ...(activeTab === 'Casos reclamados' ? [filterReclaimStatus, filterObjectionChargeType] : [])
  ].filter(f => f).length;

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setFilterInsurers([]);
    setFilterService('');
    setFilterTechnician('');
    setFilterPaymentStatus('');
    setFilterExternalStatus('');
    setFilterReclaimStatus('');
    setFilterObjectionChargeType('');
  };

  // Selection handlers
  const handleSelectAll = (e) => {
    if (!bulkSelectionEnabled) return;

    const pageIds = currentPageSelectableIds;
    if (e.target.checked) {
      setSelectedIds(prev => [...new Set([...prev, ...pageIds])]);
    } else {
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const currentPageSelectableIds = paginatedInvoices.filter(isInvoiceBulkSelectable).map(inv => inv.id);
  const selectedCount = selectedIds.length;
  const isAllSelected = currentPageSelectableIds.length > 0 && currentPageSelectableIds.every(id => selectedIds.includes(id));
  const isSomeSelected = selectedCount > 0;

  const buildApprovedBillableCopy = (invoice) => {
    const approvedAmount = Number(invoice.amount) || 0;
    const approvedSurcharge = Number(invoice.surcharge) || 0;
    return {
    date: invoice.date,
    expediente: invoice.expediente || invoice.id,
    technician: invoice.technician,
    userId: invoice.userId,
    userName: invoice.userName,
    insurer: invoice.insurer,
    service: invoice.service,
    amount: approvedAmount,
    surcharge: approvedSurcharge,
    technicianAmount: approvedAmount + approvedSurcharge,
    originalAmount: invoice.originalAmount,
    totalAmount: approvedAmount + approvedSurcharge,
    authorizedAmount: approvedAmount,
    externalStatus: invoice.externalStatus || '',
    providerName: invoice.providerName || invoice.technician,
    clientName: invoice.clientName || '',
    companyName: invoice.companyName || '',
    vehiclePlate: invoice.vehiclePlate || '',
    sourceFormat: invoice.sourceFormat || 'manual',
    status: 'pendiente',
    claimDate: invoice.claimDate,
    observation: invoice.observation,
    networkObservation: `Reclamo Aprobado. Copia de ${invoice.id}`,
    responseDate: '',
    claimTechnicianComment: invoice.claimTechnicianComment || '',
    claimTechnicianCommentDate: invoice.claimTechnicianCommentDate || '',
    claimAdminComment: invoice.claimAdminComment || '',
    claimAdminCommentDate: invoice.claimAdminCommentDate || '',
    paymentStatus: 'pending',
    paymentDate: ''
    };
  };

  const getRejectedBillableAmount = (invoice) => {
    const originalAmount = Number(invoice.originalAmount) || 0;
    if (originalAmount > 0) return originalAmount;

    const totalAmount = Number(invoice.totalAmount) || 0;
    const surcharge = Number(invoice.surcharge) || 0;
    const derivedOriginalAmount = totalAmount - surcharge;
    if (derivedOriginalAmount > 0) return derivedOriginalAmount;

    return Number(invoice.amount) || 0;
  };

  const buildRejectedBillableCopy = (invoice) => {
    const rejectedAmount = getRejectedBillableAmount(invoice);
    return {
    date: invoice.date,
    expediente: invoice.expediente || invoice.id,
    technician: invoice.technician,
    userId: invoice.userId,
    userName: invoice.userName,
    insurer: invoice.insurer,
    service: invoice.service,
    amount: rejectedAmount,
    surcharge: invoice.surcharge,
    technicianAmount: rejectedAmount + (Number(invoice.surcharge) || 0),
    originalAmount: rejectedAmount,
    totalAmount: invoice.totalAmount || (rejectedAmount + (Number(invoice.surcharge) || 0)),
    authorizedAmount: rejectedAmount,
    externalStatus: invoice.externalStatus || '',
    providerName: invoice.providerName || invoice.technician,
    clientName: invoice.clientName || '',
    companyName: invoice.companyName || '',
    vehiclePlate: invoice.vehiclePlate || '',
    sourceFormat: invoice.sourceFormat || 'manual',
    status: 'pendiente',
    claimDate: invoice.claimDate,
    observation: invoice.observation,
    networkObservation: `Reclamo Rechazado. Copia de ${invoice.id}. Se factura monto original.`,
    responseDate: '',
    claimTechnicianComment: invoice.claimTechnicianComment || '',
    claimTechnicianCommentDate: invoice.claimTechnicianCommentDate || '',
    claimAdminComment: invoice.claimAdminComment || '',
    claimAdminCommentDate: invoice.claimAdminCommentDate || '',
    paymentStatus: 'pending',
    paymentDate: ''
    };
  };

  const renderReclaimStatusBadge = (status) => {
    if (status === 'aprobado') {
      return (
        <span className="rounded-lg bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
          Reclamo aprobado
        </span>
      );
    }

    if (status === 'rechazado') {
      return (
        <span className="rounded-lg bg-red-100 px-2 py-1 text-xs font-semibold text-red-700">
          Reclamo rechazado
        </span>
      );
    }

    return null;
  };

  const renderPaymentStatusBadge = (paymentStatus) => {
    if ((paymentStatus || 'pending') === 'paid') {
      return (
        <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
          Pagado
        </span>
      );
    }

    return (
      <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
        Pendiente
      </span>
    );
  };

  const openCommentsModal = (invoice) => {
    setSelectedCommentInvoice(invoice);
    setUserCommentDraft(invoice.claimTechnicianComment || '');
    setAdminCommentDraft(invoice.claimAdminComment || '');
    setIsCommentsModalOpen(true);
  };

  const closeCommentsModal = () => {
    setIsCommentsModalOpen(false);
    setSelectedCommentInvoice(null);
    setUserCommentDraft('');
    setAdminCommentDraft('');
  };

  const handleSaveComments = async () => {
    if (!selectedCommentInvoice) return;

    const today = todayAsStorageDate();

    const updated = {
      ...selectedCommentInvoice,
      claimTechnicianComment: userCommentDraft.trim(),
      claimTechnicianCommentDate: !canManageInvoices && userCommentDraft.trim()
        ? today
        : (selectedCommentInvoice.claimTechnicianCommentDate || ''),
      claimAdminComment: adminCommentDraft.trim(),
      claimAdminCommentDate: canManageInvoices && adminCommentDraft.trim()
        ? today
        : (selectedCommentInvoice.claimAdminCommentDate || '')
    };

    if (await updateInvoiceAPI(selectedCommentInvoice.id, updated)) {
      setInvoices((prev) => prev.map((inv) => (
        inv.id === selectedCommentInvoice.id ? normalizeInvoice(updated) : inv
      )));
      addNotification('Comentarios actualizados', 'Se guardaron los comentarios del reclamo.', 'success');
      closeCommentsModal();
    } else {
      addNotification('Error', 'No fue posible guardar los comentarios.', 'error');
    }
  };

  const openCollaboratorObjection = (invoice) => {
    setSelectedInvoiceId(invoice.id);
    setObjectionAmount(invoice.amount.toString());
    setObjectionSurcharge(invoice.surcharge ? invoice.surcharge.toString() : '0');
    setObjectionReason('');
    setObjectionChargeType('');
    setObjectionKilometers('');
    setObjectionKmPrice('');
    setObjectionTollPrice('');
    setObjectionInformationalPrice('');
    setObjectionAttachmentFile(null);
    if (objectionAttachmentInputRef.current) {
      objectionAttachmentInputRef.current.value = '';
    }
    setIsObjectionModalOpen(true);
  };

  const renderInvoiceActions = (inv, mobile = false) => (
    showInvoiceActions ? (
    <div className={`flex items-center ${mobile ? 'justify-end gap-2' : 'justify-center gap-1'}`}>
      {canManageInvoices && (
        <>
          {(activeTab === importedCasesTabName || (isReclaimedTab && inv.status === 'reclamado')) && (
            <button
              data-testid={`approve-${inv.id}`}
              onClick={(e) => { e.stopPropagation(); handleApprove(inv.id); }}
              className={`rounded-xl transition-colors ${mobile ? 'p-2.5' : 'p-2'} text-emerald-600 hover:bg-emerald-50`}
              title={activeTab === importedCasesTabName ? 'Publicar para Técnico' : 'Aprobar Reclamo'}
            >
              <Check size={18} />
            </button>
          )}
          {activeTab === attendedCasesTabName && inv.status === 'atendido' && (
            <button
              data-testid={`send-billing-${inv.id}`}
              onClick={(e) => { e.stopPropagation(); handleApprove(inv.id); }}
              className={`rounded-xl transition-colors ${mobile ? 'p-2.5' : 'p-2'} text-emerald-600 hover:bg-emerald-50`}
              title="Enviar a facturación"
            >
              <Check size={18} />
            </button>
          )}
          {(activeTab === importedCasesTabName || (isReclaimedTab && inv.status === 'reclamado')) && (
            <button
              data-testid={`reject-${inv.id}`}
              onClick={(e) => { e.stopPropagation(); handleRejectClick(inv.id); }}
              className={`rounded-xl transition-colors ${mobile ? 'p-2.5' : 'p-2'} text-amber-500 hover:bg-amber-50`}
              title={activeTab === importedCasesTabName ? 'Eliminar' : 'Rechazar Reclamo'}
            >
              <X size={18} />
            </button>
          )}
          {isReclaimedTab && inv.status !== 'reclamado' && renderReclaimStatusBadge(inv.status)}
          <button
            data-testid={`delete-single-${inv.id}`}
            onClick={async (e) => {
              e.stopPropagation();
              openConfirmDialog({
                title: 'Eliminar expediente',
                message: `Se eliminará el expediente ${inv.expediente || inv.id}.`,
                confirmLabel: 'Eliminar',
                confirmClassName: 'bg-red-600 hover:bg-red-700',
                action: async () => {
                  await deleteInvoiceAPI(inv.id);
                  setInvoices(prev => prev.filter(i => i.id !== inv.id));
                  addNotification('Expediente Eliminado', 'Se eliminó el expediente', 'info');
                }
              });
            }}
            className={`rounded-xl transition-colors ${mobile ? 'p-2.5' : 'p-2'} text-red-500 hover:bg-red-50`}
            title="Eliminar expediente"
          >
            <Trash2 size={18} />
          </button>
          {isReclaimedTab && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openCommentsModal(inv);
              }}
              className={`rounded-xl transition-colors ${mobile ? 'p-2.5' : 'p-2'} text-slate-500 hover:bg-slate-100`}
              title="Ver comentarios"
            >
              <MoreHorizontal size={18} />
            </button>
          )}
        </>
      )}

      {!canManageInvoices && activeTab === 'Casos Atendidos' && inv.status === 'atendido' && (
        <>
          <button
            data-testid={`collab-approve-${inv.id}`}
            onClick={(e) => {
              e.stopPropagation();
              openConfirmDialog({
                title: 'Confirmar aceptación del caso',
                message: `¿Confirmas que revisaste el expediente ${inv.expediente || inv.id} y deseas enviarlo a facturación?`,
                confirmLabel: 'Sí, aceptar',
                confirmClassName: 'bg-emerald-600 hover:bg-emerald-700',
                action: async () => {
                  const updated = { ...inv, status: 'pendiente' };
                  if (await updateInvoiceAPI(inv.id, updated)) {
                    await refreshAfterTransaction();
                    addNotification('Caso aprobado', `El expediente ${inv.expediente || inv.id} fue enviado a facturación.`, 'success');
                  }
                }
              });
            }}
            className={`rounded-xl transition-colors ${mobile ? 'p-2.5' : 'p-2'} text-emerald-600 hover:bg-emerald-50`}
            title="Aprobar - Enviar a Facturar"
          >
            <Check size={18} />
          </button>
          <button
            data-testid={`collab-reject-${inv.id}`}
            onClick={(e) => {
              e.stopPropagation();
              openConfirmDialog({
                title: 'Confirmar declinación del caso',
                message: `¿Confirmas que revisaste el expediente ${inv.expediente || inv.id} y deseas declinarlo para registrar una objeción?`,
                confirmLabel: 'Sí, declinar',
                confirmClassName: 'bg-amber-500 hover:bg-amber-600',
                action: async () => openCollaboratorObjection(inv)
              });
            }}
            className={`rounded-xl transition-colors ${mobile ? 'p-2.5' : 'p-2'} text-amber-500 hover:bg-amber-50`}
            title="Rechazar - Enviar a Reclamados"
          >
            <X size={18} />
          </button>
        </>
      )}

      {!canManageInvoices && isReclaimedTab && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openCommentsModal(inv);
          }}
          className={`rounded-xl transition-colors ${mobile ? 'p-2.5' : 'p-2'} text-slate-500 hover:bg-slate-100`}
          title="Ver comentarios"
        >
          <MoreHorizontal size={18} />
        </button>
      )}

      {!canManageInvoices && isReclaimedTab && inv.status !== 'reclamado' && renderReclaimStatusBadge(inv.status)}

      {!canManageInvoices && !(isReclaimedTab && inv.status !== 'reclamado') && (activeTab !== 'Casos Atendidos' || inv.status !== 'atendido') && (
        <span className="text-xs text-slate-400">-</span>
      )}

      {canManageInvoices && (isBillingTab || isBilledTab) && (
        <button
          type="button"
          onClick={async (e) => {
            e.stopPropagation();
            await handleTogglePaymentStatus(inv);
          }}
          className={`rounded-xl transition-colors ${mobile ? 'px-2.5 py-1.5 text-[11px]' : 'px-2 py-1 text-xs'} font-semibold ${
            (inv.paymentStatus || 'pending') === 'paid'
              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
              : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
          }`}
          title={(inv.paymentStatus || 'pending') === 'paid' ? 'Marcar pendiente' : 'Marcar pagado'}
        >
          {(inv.paymentStatus || 'pending') === 'paid' ? 'Pendiente' : 'Pagado'}
        </button>
      )}
    </div>
    ) : null
  );

  // Delete selected
  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;

    const count = selectedIds.length;
    openConfirmDialog({
      title: 'Eliminar expedientes',
      message: `Se eliminarán ${count} expediente${count > 1 ? 's' : ''} seleccionados.`,
      confirmLabel: 'Eliminar',
      confirmClassName: 'bg-red-600 hover:bg-red-700',
      action: async () => {
        try {
          for (const id of selectedIds) {
            await deleteInvoiceAPI(id);
          }

          await refreshAfterTransaction();
          addNotification("Expedientes Eliminados", `Se eliminaron ${count} expediente${count > 1 ? 's' : ''}.`, "info");
        } catch (error) {
          console.error('Error eliminando expedientes:', error);
          addNotification('Error', 'Error al eliminar algunos expedientes.', 'error');
        }
      }
    });
  };

  const handleBulkPaymentStatus = async (nextStatus) => {
    if (selectedIds.length === 0) return;

    const selectedInvoices = invoices.filter(inv => selectedIds.includes(inv.id));
    if (selectedInvoices.length === 0) return;

    const today = todayAsStorageDate();
    const updatedInvoices = selectedInvoices.map((invoice) => ({
      ...invoice,
      paymentStatus: nextStatus,
      paymentDate: nextStatus === 'paid' ? today : ''
    }));

    try {
      const results = await Promise.all(
        updatedInvoices.map((invoice) => updateInvoiceAPI(invoice.id, invoice))
      );

      if (results.some(result => !result)) {
        addNotification('Error', 'No fue posible actualizar todos los expedientes seleccionados.', 'error');
        return;
      }

      await refreshAfterTransaction();
      addNotification(
        'Expedientes actualizados',
        `${updatedInvoices.length} expediente${updatedInvoices.length > 1 ? 's' : ''} marcado${updatedInvoices.length > 1 ? 's' : ''} como ${nextStatus === 'paid' ? 'pagado' : 'pendiente'}.`,
        'success'
      );
    } catch (error) {
      console.error('Error actualizando pago en lote:', error);
      addNotification('Error', 'No fue posible actualizar el estado de pago.', 'error');
    }
  };

  const handleBulkAssignTechnician = async () => {
    if (!bulkTechnicianId) {
      addNotification('Seleccion requerida', 'Debes elegir un tecnico para asignar en lote.', 'warning');
      return;
    }

    const technician = users.find((user) => user.id === bulkTechnicianId);
    if (!technician) {
      addNotification('Error', 'No se encontro el tecnico seleccionado.', 'error');
      return;
    }

    const targetInvoices = invoices.filter(
      (invoice) => selectedIds.includes(invoice.id) && invoice.status === 'importado'
    );

    if (targetInvoices.length === 0) {
      addNotification('Sin cambios', 'Solo puedes asignar tecnico en lote a casos importados.', 'warning');
      return;
    }

    const updatedInvoices = targetInvoices.map((invoice) => ({
      ...invoice,
      technician: technician.name,
      userId: technician.id,
      userName: technician.name
    }));

    try {
      await axios.post(`${API_URL}/api/invoices/bulk-assign`, {
        ids: updatedInvoices.map((invoice) => invoice.id),
        userId: technician.id,
        userName: technician.name
      });

      const updatedMap = new Map(updatedInvoices.map((invoice) => [invoice.id, normalizeInvoice(invoice)]));
      setInvoices((prev) => prev.map((invoice) => updatedMap.get(invoice.id) || invoice));
      setSelectedIds([]);
      setBulkTechnicianId('');
      addNotification(
        'Tecnico asignado',
        `${updatedInvoices.length} expediente${updatedInvoices.length > 1 ? 's' : ''} actualizado${updatedInvoices.length > 1 ? 's' : ''}.`,
        'success'
      );
    } catch (error) {
      console.error('Error asignando tecnico en lote:', error);
      addNotification('Error', 'No fue posible asignar tecnico en lote.', 'error');
    }
  };

  const handleBulkPublishImported = async () => {
    if (selectedIds.length === 0) {
      addNotification('Sin cambios', 'No hay casos importados seleccionados para publicar.', 'warning');
      return;
    }

    try {
      const { data } = await axios.post(`${API_URL}/api/invoices/bulk-transition`, {
        ids: selectedIds,
        fromStatus: 'importado',
        toStatus: 'atendido'
      });
      if ((data.matched || 0) !== selectedIds.length) {
        addNotification('Error', 'No fue posible publicar todos los casos seleccionados.', 'error');
        return;
      }
      const updatedCount = data.updated || data.matched || 0;
      await refreshAfterTransaction();
      addNotification(
        'Casos publicados',
        `${updatedCount} caso${updatedCount !== 1 ? 's' : ''} publicado${updatedCount !== 1 ? 's' : ''} para técnico.`,
        'success'
      );
    } catch (error) {
      console.error('Error publicando casos importados en lote:', error);
      addNotification('Error', 'No fue posible publicar los casos seleccionados.', 'error');
    }
  };

  const handleBulkSendToBilling = async () => {
    if (selectedIds.length === 0) {
      addNotification('Sin cambios', 'No hay casos atendidos seleccionados para enviar a facturación.', 'warning');
      return;
    }

    try {
      const { data } = await axios.post(`${API_URL}/api/invoices/bulk-transition`, {
        ids: selectedIds,
        fromStatus: 'atendido',
        toStatus: 'pendiente'
      });
      if ((data.matched || 0) !== selectedIds.length) {
        addNotification('Error', 'No fue posible enviar todos los casos seleccionados a facturación.', 'error');
        return;
      }
      const updatedCount = data.updated || data.matched || 0;
      await refreshAfterTransaction();
      addNotification(
        'Casos enviados a facturación',
        `${updatedCount} caso${updatedCount !== 1 ? 's' : ''} enviado${updatedCount !== 1 ? 's' : ''} a facturación.`,
        'success'
      );
    } catch (error) {
      console.error('Error enviando casos atendidos a facturación en lote:', error);
      addNotification('Error', 'No fue posible enviar los casos seleccionados a facturación.', 'error');
    }
  };

  const handleBulkResolveReclaims = async (resolution) => {
    const targetInvoices = invoices.filter(
      (invoice) => selectedIds.includes(invoice.id) && invoice.status === 'reclamado'
    );

    if (targetInvoices.length === 0) {
      addNotification('Sin cambios', 'Solo puedes procesar en lote reclamos pendientes.', 'warning');
      return;
    }

    try {
      const updatedInvoices = [];
      const createdInvoices = [];

      for (const invoice of targetInvoices) {
        const updatedInvoice = {
          ...invoice,
          status: resolution === 'approve' ? 'aprobado' : 'rechazado'
        };

        const updatedOk = await updateInvoiceAPI(invoice.id, updatedInvoice);
        if (!updatedOk) {
          throw new Error(`No fue posible actualizar el reclamo ${invoice.id}`);
        }

        const billableCopy =
          resolution === 'approve'
            ? buildApprovedBillableCopy(invoice)
            : buildRejectedBillableCopy(invoice);

        const createdInvoice = await createInvoiceAPI(billableCopy, true);
        if (!createdInvoice) {
          throw new Error(`No fue posible crear la copia facturable del reclamo ${invoice.id}`);
        }

        updatedInvoices.push(normalizeInvoice(updatedInvoice));
        createdInvoices.push(normalizeInvoice(createdInvoice));
      }

      await refreshAfterTransaction();
      addNotification(
        resolution === 'approve' ? 'Reclamos aprobados' : 'Reclamos rechazados',
        `${updatedInvoices.length} reclamo${updatedInvoices.length > 1 ? 's' : ''} procesado${updatedInvoices.length > 1 ? 's' : ''} en lote.`,
        'success'
      );
    } catch (error) {
      console.error('Error resolviendo reclamos en lote:', error);
      addNotification('Error', 'No fue posible procesar todos los reclamos seleccionados.', 'error');
    }
  };

  const handleBulkDateUpdate = async () => {
    const normalizedDate = normalizeDateForStorage(bulkDateValue);
    if (!normalizedDate) {
      addNotification('Fecha requerida', 'Selecciona una fecha válida para continuar.', 'warning');
      return;
    }

    const targetInvoices = invoices.filter(
      (invoice) => selectedIds.includes(invoice.id) && isInvoiceBulkSelectable(invoice)
    );

    if (targetInvoices.length === 0) {
      addNotification('Sin cambios', 'No hay casos válidos seleccionados para cambiar la fecha.', 'warning');
      return;
    }

    try {
      const updatedInvoices = targetInvoices.map((invoice) => ({
        ...invoice,
        date: normalizedDate
      }));

      const results = await Promise.all(
        updatedInvoices.map((invoice) => updateInvoiceAPI(invoice.id, invoice))
      );

      if (results.some((result) => !result)) {
        addNotification('Error', 'No fue posible actualizar la fecha en todos los casos seleccionados.', 'error');
        return;
      }

      const updatedMap = new Map(updatedInvoices.map((invoice) => [invoice.id, normalizeInvoice(invoice)]));
      setInvoices((prev) => prev.map((invoice) => updatedMap.get(invoice.id) || invoice));
      setSelectedIds([]);
      setBulkDateValue('');
      setIsBulkDateModalOpen(false);
      addNotification(
        'Fecha actualizada',
        `Se actualizó la fecha de ${updatedInvoices.length} caso${updatedInvoices.length > 1 ? 's' : ''}.`,
        'success'
      );
    } catch (error) {
      console.error('Error actualizando fecha en lote:', error);
      addNotification('Error', 'No fue posible actualizar la fecha seleccionada.', 'error');
    }
  };

  // Clear selection when changing tabs
  useEffect(() => {
    setSelectedIds([]);
    setBulkTechnicianId('');
    setBulkDateValue('');
    setIsBulkDateModalOpen(false);
  }, [activeTab]);

  // Export
  const handleExport = async () => {
    if (serverTotal === 0) return;
    const normalizedTab = activeTab.toLowerCase();
    const view = normalizedTab === 'casos reclamados' ? 'reclaimed'
      : normalizedTab === 'casos a facturar' ? 'billable'
        : normalizedTab === 'casos facturados' ? 'billed'
          : normalizedTab === 'casos atendidos' ? 'attended' : 'imported';
    const res = await axios.get(`${API_URL}/api/invoices`, { params: {
      view, start_date: startDate, end_date: endDate, all_records: true,
      search: debouncedSearch, insurers: filterInsurers.join('|'), service: filterService,
      technician: filterTechnician, payment_status: filterPaymentStatus,
      external_status: filterExternalStatus, reclaim_status: filterReclaimStatus,
      objection_type: filterObjectionChargeType,
      user_id: canManageInvoices ? '' : (currentUser?.id || ''),
      user_name: canManageInvoices ? '' : (currentUser?.name || ''),
    }});
    const exportInvoices = (res.data?.items || []).map(normalizeInvoice);
    const dataToExport = exportInvoices.map(inv => ({
      Fecha: formatDateDisplay(inv.date || inv.claimDate),
      Expediente: inv.expediente || inv.id,
      'Técnico': inv.technician,
      'Usuario Asignado': inv.userName || 'No asignado',
      Aseguradora: inv.insurer,
      Servicio: inv.service,
      Monto: inv.amount,
      Recargo: inv.surcharge || 0,
      Total: (Number(inv.amount) || 0) + (Number(inv.surcharge) || 0),
      'Monto Autorizado': inv.authorizedAmount || 0,
      Estatus: inv.status,
      'Fecha de Pago': formatDateDisplay(inv.paymentDate),
      ...(activeTab === 'Casos reclamados' ? {
        'Comentario Técnico': inv.claimTechnicianComment || '',
        'Fecha Comentario Técnico': formatDateDisplay(inv.claimTechnicianCommentDate || inv.claimDate),
        'Comentario Administrador': inv.claimAdminComment || '',
        'Fecha Comentario Administrador': formatDateDisplay(inv.claimAdminCommentDate)
      } : {}),
      Observaciones: inv.observation,
      'Observaciones Admin': inv.networkObservation
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Facturación");
    const fileName = `Reporte_${activeTab.replace(/\s/g, '_')}_${todayAsStorageDate()}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const renderImportFeedbackDetails = () => {
    if (!importFeedback) return null;

    const duplicates = Array.isArray(importFeedback.duplicates) ? importFeedback.duplicates : [];
    const preview = Array.isArray(importFeedback.duplicatePreview) ? importFeedback.duplicatePreview : [];

    if (!duplicates.length) return null;

    return (
      <div className="mt-3 space-y-3">
        <div className="rounded-xl bg-white/60 px-3 py-2 text-xs leading-5">
          <p className="font-medium">Registros detectados:</p>
          {preview.map((item, index) => (
            <p key={`${item}-${index}`}>{`${index + 1}. ${item}`}</p>
          ))}
          {duplicates.length > preview.length && (
            <p>{`... y ${duplicates.length - preview.length} más.`}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => downloadDuplicateReport(duplicates)}
          className="inline-flex items-center gap-2 rounded-xl border border-current/20 bg-white/70 px-3 py-2 text-xs font-medium transition hover:bg-white"
        >
          <Download size={14} />
          <span>{`Descargar duplicados (${duplicates.length})`}</span>
        </button>
      </div>
    );
  };

  return (
    <div data-testid="invoices-page" className="flex h-full min-h-0 flex-col gap-6 overflow-hidden text-sm">
      {importFeedback && !isUploadModalOpen && (
        <div
          className={`fixed right-6 top-6 z-50 w-[calc(100vw-3rem)] max-w-md rounded-2xl border px-4 py-3 shadow-2xl ${
            importFeedback.type === 'warning'
              ? 'border-amber-200 bg-amber-50 text-amber-800'
              : importFeedback.type === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold">{importFeedback.title}</p>
              <p className="text-sm leading-6">{importFeedback.message}</p>
              {renderImportFeedbackDetails()}
            </div>
            <button
              type="button"
              onClick={() => setImportFeedback(null)}
              className="rounded-lg p-1 transition hover:bg-black/5"
              aria-label="Cerrar aviso"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-shrink-0 flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-800">{"Facturación"}</h1>
          <p className="mt-1 text-sm text-slate-500">{"Gestiona expedientes y facturación"}</p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto justify-end">
          {/* Refresh button */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 bg-slate-600 text-white px-4 py-2.5 rounded-xl hover:bg-slate-700 transition-all shadow-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            title="Actualizar datos"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            <span>{refreshing ? 'Actualizando...' : 'Refrescar'}</span>
          </button>

          {bulkSelectionEnabled && isSomeSelected && (
            <button
              type="button"
              onClick={() => setIsBulkDateModalOpen(true)}
              className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 font-medium text-white shadow-sm transition-all hover:bg-indigo-700"
            >
              <Calendar size={18} />
              <span>Cambiar fecha ({selectedCount})</span>
            </button>
          )}

          {importedSelectionEnabled && isSomeSelected && (
            <>
              <select
                value={bulkTechnicianId}
                onChange={(e) => setBulkTechnicianId(e.target.value)}
                className="min-w-[220px] rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="">Asignar tecnico...</option>
                {technicianUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleBulkAssignTechnician}
                className="flex items-center gap-2 rounded-xl bg-slate-600 px-4 py-2.5 font-medium text-white shadow-sm transition-all hover:bg-slate-700"
              >
                <UserIcon size={18} />
                <span>Asignar tecnico ({selectedCount})</span>
              </button>
              <button
                type="button"
                onClick={handleBulkPublishImported}
                className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 font-medium text-white shadow-sm transition-all hover:bg-emerald-700"
              >
                <Check size={18} />
                <span>Publicar ({selectedCount})</span>
              </button>
              <button
                data-testid="delete-selected-btn"
                onClick={handleDeleteSelected}
                className="flex items-center gap-2 bg-red-500 text-white px-4 py-2.5 rounded-xl hover:bg-red-600 transition-all shadow-sm font-medium"
              >
                <Trash2 size={18} />
                <span>Eliminar ({selectedCount})</span>
              </button>
            </>
          )}

          {reclaimedSelectionEnabled && isSomeSelected && (
            <>
              <button
                type="button"
                onClick={() => handleBulkResolveReclaims('approve')}
                className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 font-medium text-white shadow-sm transition-all hover:bg-emerald-700"
              >
                <Check size={18} />
                <span>Aprobar reclamos ({selectedCount})</span>
              </button>
              <button
                type="button"
                onClick={() => handleBulkResolveReclaims('reject')}
                className="flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 font-medium text-white shadow-sm transition-all hover:bg-amber-600"
              >
                <X size={18} />
                <span>Rechazar reclamos ({selectedCount})</span>
              </button>
              <button
                data-testid="delete-selected-btn"
                onClick={handleDeleteSelected}
                className="flex items-center gap-2 bg-red-500 text-white px-4 py-2.5 rounded-xl hover:bg-red-600 transition-all shadow-sm font-medium"
              >
                <Trash2 size={18} />
                <span>Eliminar ({selectedCount})</span>
              </button>
            </>
          )}

          {attendedSelectionEnabled && isSomeSelected && (
            <>
              <button
                type="button"
                onClick={handleBulkSendToBilling}
                className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 font-medium text-white shadow-sm transition-all hover:bg-emerald-700"
              >
                <Check size={18} />
                <span>Enviar a facturar ({selectedCount})</span>
              </button>
              <button
                data-testid="delete-selected-btn"
                onClick={handleDeleteSelected}
                className="flex items-center gap-2 bg-red-500 text-white px-4 py-2.5 rounded-xl hover:bg-red-600 transition-all shadow-sm font-medium"
              >
                <Trash2 size={18} />
                <span>Eliminar ({selectedCount})</span>
              </button>
            </>
          )}

          {billingSelectionEnabled && isSomeSelected && (
            <>
              <button
                type="button"
                onClick={() => handleBulkPaymentStatus('paid')}
                className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 font-medium text-white shadow-sm transition-all hover:bg-emerald-700"
              >
                <Check size={18} />
                <span>Marcar pagado ({selectedCount})</span>
              </button>
              <button
                type="button"
                onClick={() => handleBulkPaymentStatus('pending')}
                className="flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 font-medium text-white shadow-sm transition-all hover:bg-amber-600"
              >
                <RefreshCw size={18} />
                <span>Marcar pendiente ({selectedCount})</span>
              </button>
              <button
                data-testid="delete-selected-btn"
                onClick={handleDeleteSelected}
                className="flex items-center gap-2 bg-red-500 text-white px-4 py-2.5 rounded-xl hover:bg-red-600 transition-all shadow-sm font-medium"
              >
                <Trash2 size={18} />
                <span>Eliminar ({selectedCount})</span>
              </button>
            </>
          )}
          {billedSelectionEnabled && isSomeSelected && (
            <>
              <button
                type="button"
                onClick={() => handleBulkPaymentStatus('pending')}
                className="flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 font-medium text-white shadow-sm transition-all hover:bg-amber-600"
              >
                <RefreshCw size={18} />
                <span>Marcar pendiente ({selectedCount})</span>
              </button>
              <button
                data-testid="delete-selected-btn"
                onClick={handleDeleteSelected}
                className="flex items-center gap-2 bg-red-500 text-white px-4 py-2.5 rounded-xl hover:bg-red-600 transition-all shadow-sm font-medium"
              >
                <Trash2 size={18} />
                <span>Eliminar ({selectedCount})</span>
              </button>
            </>
          )}
          {(activeTab === 'Casos reclamados' || activeTab === 'Casos a facturar' || activeTab === 'Casos facturados') && filteredInvoices.length > 0 && (
            <button
              data-testid="export-btn"
              onClick={handleExport}
              className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2.5 rounded-xl hover:bg-emerald-700 transition-all shadow-sm font-medium"
            >
              <Download size={18} />
              <span>Exportar</span>
            </button>
          )}
          {canManageInvoices && (
            <>
              <button
                data-testid="new-invoice-btn"
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-xl hover:bg-blue-700 transition-all shadow-sm font-medium"
              >
                <Plus size={18} />
                <span>Nuevo</span>
              </button>
              <button
                data-testid="import-btn"
                onClick={() => { setImportFeedback(null); setIsUploadModalOpen(true); }}
                className="flex items-center gap-2 bg-primary text-white px-4 py-2.5 rounded-xl hover:bg-primary-hover transition-all shadow-lg shadow-primary/25 font-medium"
              >
                <Upload size={18} />
                <span>Importar</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-[-1px] w-full flex-shrink-0 overflow-x-auto pb-1 hide-scrollbar">
        <div className="flex items-end gap-1 border-b border-slate-200 pt-2 min-w-max">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              data-testid={`tab-${tab.name.replace(/\s/g, '-').toLowerCase()}`}
              onClick={() => setActiveTab(tab.name)}
              className={`
                px-6 py-3 font-semibold text-sm rounded-t-xl transition-all duration-200 whitespace-nowrap
                ${activeTab === tab.name
                  ? 'bg-white text-primary border-t-2 border-x border-primary border-slate-200 shadow-sm relative translate-y-[1px] z-10'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }
              `}
            >
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex-shrink-0 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        {/* Filter Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <h3 className="text-sm font-medium text-slate-700">Filtros</h3>
            {activeFiltersCount > 0 && (
              <span className="bg-primary text-white text-xs font-bold px-2 py-1 rounded-full">
                {activeFiltersCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeFiltersCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-700 hover:underline"
              >
                <X size={16} />
                Limpiar todos
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowFilters((prev) => !prev)}
              className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100"
            >
              {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              <span>{showFilters ? 'Ocultar' : 'Mostrar'}</span>
            </button>
          </div>
        </div>

        {showFilters && (
          <>
        {/* Filter Controls */}
        <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-6">
          {/* Insurer Filter */}
          <div>
            <label className="mb-1 block text-[11px] font-medium text-slate-600">Aseguradora</label>
            <details className="group relative">
              <summary className="flex w-full cursor-pointer list-none items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary [&::-webkit-details-marker]:hidden">
                <span className="truncate">
                  {filterInsurers.length === 0
                    ? 'Todas las aseguradoras'
                    : `${filterInsurers.length} aseguradora${filterInsurers.length !== 1 ? 's' : ''}`}
                </span>
                <ChevronDown size={16} className="shrink-0 transition-transform group-open:rotate-180" />
              </summary>
              <div className="absolute left-0 top-full z-40 mt-1 min-w-full overflow-hidden rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
                <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={filterInsurers.length === 0}
                    onChange={() => setFilterInsurers([])}
                    className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                  />
                  <span className="whitespace-nowrap">Todas las aseguradoras</span>
                </label>
                <div className="my-1 border-t border-slate-100" />
                {uniqueInsurers.map((insurer) => (
                  <label key={insurer} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm text-slate-700 hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={filterInsurers.includes(insurer)}
                      onChange={() => toggleInsurerFilter(insurer)}
                      className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                    />
                    <span className="whitespace-nowrap">{insurer}</span>
                  </label>
                ))}
              </div>
            </details>
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-medium text-slate-600">Tipo de servicio</label>
            <select
              value={filterService}
              onChange={(e) => setFilterService(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Todos los servicios</option>
              {uniqueServices.map((service) => (
                <option key={service} value={service}>{service}</option>
              ))}
            </select>
          </div>

          {/* Technician Filter */}
          {canManageInvoices && (
            <div>
            <label className="block text-xs font-semibold text-slate-600 mb-2">{"Técnico"}</label>
            <select
              value={filterTechnician}
              onChange={(e) => setFilterTechnician(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">{"Todos los técnicos"}</option>
              {uniqueTechnicians.map(tech => (
                <option key={tech} value={tech}>{tech}</option>
              ))}
            </select>
            </div>
          )}

          {activeTab === 'Casos reclamados' && (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-2">Estado del reclamo</label>
                <select
                  value={filterReclaimStatus}
                  onChange={(e) => setFilterReclaimStatus(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Todos los estados</option>
                  {RECLAIM_STATUS_FILTERS.map((status) => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-2">Motivo de objeción</label>
                <select
                  value={filterObjectionChargeType}
                  onChange={(e) => setFilterObjectionChargeType(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Todos los motivos</option>
                  {Object.entries(OBJECTION_CHARGE_TYPES).map(([type, config]) => (
                    <option key={type} value={type}>{config.label}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Date Range - Start */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-2">Fecha Desde</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Date Range - End */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-2">Fecha Hasta</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mt-2">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="search-invoices"
            type="text"
            placeholder="Buscar por expediente, servicio..."
            className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Active Filters Chips */}
        {activeFiltersCount > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5 border-t border-slate-100 pt-2">
            <span className="text-xs text-slate-500 font-medium">Filtros activos:</span>
            {filterInsurers.map((insurer) => (
              <span key={insurer} className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs font-medium px-3 py-1 rounded-full">
                Aseguradora: {insurer}
                <button onClick={() => toggleInsurerFilter(insurer)} className="hover:bg-blue-200 rounded-full p-0.5">
                  <X size={12} />
                </button>
              </span>
            ))}
            {filterService && (
              <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs font-medium px-3 py-1 rounded-full">
                Servicio: {filterService}
                <button onClick={() => setFilterService('')} className="hover:bg-emerald-200 rounded-full p-0.5">
                  <X size={12} />
                </button>
              </span>
            )}
            {canManageInvoices && filterTechnician && (
              <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs font-medium px-3 py-1 rounded-full">
                {"Técnico: "}{filterTechnician}
                <button onClick={() => setFilterTechnician('')} className="hover:bg-emerald-200 rounded-full p-0.5">
                  <X size={12} />
                </button>
              </span>
            )}
            {activeTab === 'Casos reclamados' && filterReclaimStatus && (
              <span className="inline-flex items-center gap-1 bg-sky-100 text-sky-700 text-xs font-medium px-3 py-1 rounded-full">
                Estado: {formatReclaimStatusFilter(filterReclaimStatus)}
                <button onClick={() => setFilterReclaimStatus('')} className="hover:bg-sky-200 rounded-full p-0.5">
                  <X size={12} />
                </button>
              </span>
            )}
            {activeTab === 'Casos reclamados' && filterObjectionChargeType && (
              <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 text-xs font-medium px-3 py-1 rounded-full">
                Motivo: {formatObjectionChargeType(filterObjectionChargeType)}
                <button onClick={() => setFilterObjectionChargeType('')} className="hover:bg-orange-200 rounded-full p-0.5">
                  <X size={12} />
                </button>
              </span>
            )}
            {startDate && (
              <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-medium px-3 py-1 rounded-full">
                Desde: {startDate}
                <button onClick={() => setStartDate('')} className="hover:bg-amber-200 rounded-full p-0.5">
                  <X size={12} />
                </button>
              </span>
            )}
            {endDate && (
              <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-medium px-3 py-1 rounded-full">
                Hasta: {endDate}
                <button onClick={() => setEndDate('')} className="hover:bg-amber-200 rounded-full p-0.5">
                  <X size={12} />
                </button>
              </span>
            )}
            {searchTerm && (
              <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 text-xs font-medium px-3 py-1 rounded-full">
                {`Búsqueda: "${searchTerm}"`}
                <button onClick={() => setSearchTerm('')} className="hover:bg-purple-200 rounded-full p-0.5">
                  <X size={12} />
                </button>
              </span>
            )}
          </div>
        )}

        {/* Results Counter */}
        <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
          <p className="text-xs text-slate-600">
            <span className="font-semibold text-primary">{serverTotal}</span> expediente{serverTotal !== 1 ? 's' : ''} encontrado{serverTotal !== 1 ? 's' : ''}
          </p>
          {shouldApplyRecentWindow && (
            <span className="rounded-full bg-amber-50 px-3 py-1 text-[11px] font-medium text-amber-700">
              Mostrando últimos 15 días
            </span>
          )}
        </div>
          </>
        )}
      </div>

      {/* Results */}
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {loading && (
          <div className="flex min-h-64 flex-col items-center justify-center gap-3 p-12 text-center">
            <Loader2 size={40} className="animate-spin text-primary" />
            <p className="text-sm font-medium text-slate-600">Cargando expedientes…</p>
          </div>
        )}

        {!loading && loadError && (
          <div className="flex min-h-64 flex-col items-center justify-center gap-3 p-12 text-center">
            <FileText size={48} className="text-red-200" />
            <p className="max-w-md text-sm font-medium text-red-600">{loadError}</p>
            <button
              type="button"
              onClick={() => fetchInvoices()}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover"
            >
              <RefreshCw size={16} />
              Reintentar
            </button>
          </div>
        )}

        {!loading && !loadError && filteredInvoices.length > 0 && (
          <div className="border-b border-slate-200 px-4 py-3">
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        )}

        {!loading && !loadError && <div className="space-y-3 p-3 lg:hidden">
          {paginatedInvoices.map(inv => (
            <article
              key={inv.id}
              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="mb-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Expediente</p>
                  <div className="mt-1 font-semibold">{renderEditableField(inv, 'expediente', inv.expediente || inv.id)}</div>
                </div>
                <div className="flex items-center gap-3">
                  {bulkSelectionEnabled && (
                    <label className="inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(inv.id)}
                        onChange={() => handleSelectOne(inv.id)}
                        disabled={!isInvoiceBulkSelectable(inv)}
                        className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
                      />
                    </label>
                  )}
                  <div className="shrink-0">{renderEditableField(inv, 'date', formatDateDisplay(inv.date || inv.claimDate), { type: 'date' })}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                <div className="col-span-2">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Técnico</p>
                  {editingCell?.id === inv.id && editingCell?.field === 'technician' ? (
                    <select
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={saveEdit}
                      autoFocus
                      className="mt-1 w-full rounded-lg border border-primary p-2 text-sm focus:outline-none"
                    >
                      <option value="">-- Sin asignar --</option>
                      {activeUsers.map(u => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                  ) : (
                    <button
                      type="button"
                      onClick={() => canManageInvoices && startEditing(inv, 'technician')}
                      className={`mt-1 flex items-center gap-2 text-left ${canManageInvoices ? 'text-slate-700' : 'text-slate-600'}`}
                    >
                      <span className="truncate">{inv.technician}</span>
                      {canManageInvoices && <Pencil size={13} className="text-slate-400" />}
                    </button>
                  )}
                </div>
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Aseguradora</p>
                  <div className="mt-1">{renderEditableField(inv, 'insurer', inv.insurer, { options: INSURER_OPTIONS })}</div>
                </div>
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Servicio</p>
                  <div className="mt-1">{renderEditableField(inv, 'service', inv.service, { options: MANUAL_SERVICE_OPTIONS })}</div>
                </div>
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Costo</p>
                  {editingCell?.id === inv.id && editingCell?.field === 'amount' ? (
                    <input
                      type="number"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={saveEdit}
                      onKeyDown={handleEditKeyDown}
                      autoFocus
                      className="mt-1 w-full rounded-lg border border-primary px-2 py-1 text-sm focus:outline-none"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => isCostEditable && startEditing(inv, 'amount')}
                      className="mt-1 inline-flex items-center gap-2 font-medium text-slate-900"
                    >
                      ${formatCurrency(inv.amount)}
                      {isCostEditable && <Pencil size={13} className="text-slate-400" />}
                    </button>
                  )}
                </div>
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Recargo</p>
                  <p className="mt-1 font-medium text-slate-900">${formatCurrency(inv.surcharge)}</p>
                </div>
              </div>

              {showInvoiceActions && (
                <div className="mt-4 border-t border-slate-100 pt-3">
                  {renderInvoiceActions(inv, true)}
                </div>
              )}
            </article>
          ))}

          {activeTab === 'Casos a facturar' && filteredInvoices.length > 0 && (
            <div className="rounded-2xl border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-transparent p-4 shadow-sm">
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3 rounded-xl bg-white/80 px-4 py-3">
                  <span className="text-xs font-bold uppercase tracking-wide text-primary">Subtotal</span>
                  <span className="text-lg font-black text-primary">${formatCurrency(totalCostAmount + totalSurcharge)}</span>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-xl bg-primary/10 px-4 py-3">
                  <span className="text-xs font-bold uppercase tracking-wide text-primary">Total a pagar</span>
                  <span className="text-xl font-black text-primary">${formatCurrency(totalTechnicianAmount)}</span>
                </div>
              </div>
            </div>
          )}
        </div>}

        {!loading && !loadError && <div className="hidden overflow-auto lg:block">
          <table className="w-full text-left min-w-[780px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                  {bulkSelectionEnabled && (
                    <th className="px-4 py-4 text-center">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                      aria-label="Seleccionar expedientes de la página"
                    />
                  </th>
                )}
                <th className="px-4 py-4 font-medium text-slate-600 text-sm">Fecha</th>
                <th className="px-4 py-4 font-medium text-slate-600 text-sm">Expediente</th>
                <th className="px-4 py-4 font-medium text-slate-600 text-sm">{"Técnico"}</th>
                <th className="px-4 py-4 font-medium text-slate-600 text-sm">Aseguradora</th>
                <th className="px-4 py-4 font-medium text-slate-600 text-sm">Servicio</th>
                <th className="px-4 py-4 font-medium text-slate-600 text-sm">Costo</th>
                <th className="px-4 py-4 font-medium text-slate-600 text-sm">Recargo</th>
                {showInvoiceActions && (
                  <th className="px-4 py-4 font-medium text-slate-600 text-sm text-center">Acciones</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedInvoices.map(inv => (
                <tr
                  key={inv.id}
                  className={`hover:bg-slate-50/50 transition-colors group ${selectedIds.includes(inv.id) ? 'bg-primary/5' : ''}`}
                >
                  {bulkSelectionEnabled && (
                    <td className="px-4 py-4 text-center">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(inv.id)}
                        onChange={() => handleSelectOne(inv.id)}
                        disabled={!isInvoiceBulkSelectable(inv)}
                        className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label={`Seleccionar expediente ${inv.expediente || inv.id}`}
                      />
                    </td>
                  )}
                  <td className="px-4 py-4 text-slate-600 text-sm">{renderEditableField(inv, 'date', formatDateDisplay(inv.date || inv.claimDate), { type: 'date' })}</td>
                  <td className="px-4 py-4 font-normal text-slate-800 text-sm">
                    {renderEditableField(inv, 'expediente', inv.expediente || inv.id)}
                  </td>
                  <td
                    className={`px-4 py-4 text-slate-600 text-sm ${canManageInvoices ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                    onClick={() => canManageInvoices && startEditing(inv, 'technician')}
                  >
                    {editingCell?.id === inv.id && editingCell?.field === 'technician' ? (
                      <select
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={saveEdit}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                        className="w-full p-1 border border-primary rounded-lg text-sm focus:outline-none"
                      >
                        <option value="">-- Sin asignar --</option>
                        {activeUsers.map(u => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </select>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="truncate">{inv.technician}</span>
                        {canManageInvoices && (
                          <Pencil size={12} className="shrink-0 text-slate-400 opacity-0 transition-opacity group-hover:opacity-100" />
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-slate-600 text-sm">{renderEditableField(inv, 'insurer', inv.insurer, { options: INSURER_OPTIONS })}</td>
                  <td className="px-4 py-4 text-slate-600 text-sm">{renderEditableField(inv, 'service', inv.service, { options: MANUAL_SERVICE_OPTIONS })}</td>
                  <td
                    className={`px-4 py-4 font-normal text-slate-800 text-sm ${isCostEditable ? 'cursor-pointer' : ''}`}
                    onClick={() => isCostEditable && startEditing(inv, 'amount')}
                  >
                    {editingCell?.id === inv.id && editingCell?.field === 'amount' ? (
                      <div className="flex items-center">
                        <span className="text-slate-400 mr-1">$</span>
                        <input
                          type="number"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={saveEdit}
                          onKeyDown={handleEditKeyDown}
                          autoFocus
                          className="w-24 px-2 py-1 border border-primary rounded-lg focus:outline-none text-sm"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        ${formatCurrency(inv.amount)}
                        {isCostEditable && <Pencil size={14} className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />}
                      </div>
                    )}
                  </td>
                  <td
                    className={`px-4 py-4 font-normal text-slate-800 text-sm ${isStandardEditable ? 'cursor-pointer' : ''}`}
                    onClick={() => isStandardEditable && startEditing(inv, 'surcharge')}
                  >
                    {editingCell?.id === inv.id && editingCell?.field === 'surcharge' ? (
                      <div className="flex items-center">
                        <span className="text-slate-400 mr-1">$</span>
                        <input
                          type="number"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={saveEdit}
                          onKeyDown={handleEditKeyDown}
                          autoFocus
                          className="w-24 px-2 py-1 border border-primary rounded-lg focus:outline-none text-sm"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        ${formatCurrency(inv.surcharge)}
                        {isStandardEditable && <Pencil size={14} className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />}
                      </div>
                    )}
                  </td>
                  {showInvoiceActions && (
                    <td className="px-4 py-4 text-center">
                      {renderInvoiceActions(inv)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {activeTab === 'Casos a facturar' && filteredInvoices.length > 0 && (
              <tfoot className="bg-gradient-to-r from-primary/5 to-transparent border-t-2 border-primary/20">
                <tr>
                  <td colSpan={billingSelectionEnabled ? 7 : 5} className="px-4 py-4 text-right font-bold text-primary text-sm">SUBTOTAL</td>
                  <td className="px-4 py-4 font-bold text-primary text-lg">${formatCurrency(totalCostAmount)}</td>
                  <td className="px-4 py-4 font-bold text-primary text-lg">${formatCurrency(totalSurcharge)}</td>
                  <td className="px-4 py-4"></td>
                </tr>
                <tr className="bg-primary/10 border-t border-primary/20">
                  <td colSpan={billingSelectionEnabled ? 7 : 5} className="px-4 py-4 text-right font-black text-primary text-sm uppercase tracking-wide">TOTAL A PAGAR</td>
                  <td colSpan={2} className="px-4 py-4 font-black text-primary text-xl">${formatCurrency(totalTechnicianAmount)}</td>
                  <td className="px-4 py-4"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>}

        {!loading && !loadError && filteredInvoices.length === 0 && (
          <div className="p-12 text-center">
            <FileText size={48} className="mx-auto mb-4 text-slate-200" />
            <p className="mb-1 text-sm font-medium text-slate-600">{"No hay expedientes en esta categoría"}</p>
            <p className="text-sm text-slate-500">
              {canManageInvoices ? "Prueba cambiando de pestaña o ajustando la búsqueda." : "No tienes expedientes asignados."}
            </p>
          </div>
        )}
      </div>

      {/* Loans Summary Section - Show in "Casos a facturar" tab */}
      {activeTab === 'Casos a facturar' && filteredInvoices.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mt-6">
          <div className="p-6 bg-gradient-to-r from-amber-50 to-white border-b border-amber-200">
            <h3 className="text-sm font-medium text-slate-800">{"Préstamos / Descuentos Pendientes"}</h3>
            <p className="text-sm text-slate-600 mt-1">
              {canManageInvoices ? 'Descuentos que se aplicarán al pago de cada técnico' : 'Tus descuentos pendientes'}
            </p>
          </div>

          <div className="p-6 space-y-6">
            {/* Totals grouped by the backend across the complete filtered result. */}
            {[...new Set(serverTechnicianGroups.map(group => group.technician).filter(Boolean))].map(techName => {
                // Filter by current user if not managing invoices
                if (!canManageInvoices && techName !== currentUser?.name) return null;

              const techGroups = serverTechnicianGroups.filter(group => group.technician === techName);
              const techLoans = loans.filter(loan => loan.technicianName === techName);

              // Calculations
              const totalGeneral = techGroups.reduce((sum, group) => sum + getInvoiceTechnicianAmount(group), 0);
              const loansTotal = techLoans.reduce((sum, loan) => sum + (loan.amount || 0), 0);
              const netTotal = totalGeneral - loansTotal;

              // Only show if technician has loans
              if (techLoans.length === 0) return null;

              return (
                <div key={techName} className="border border-slate-200 rounded-xl p-6 bg-slate-50/50">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="flex items-center gap-2 text-sm font-medium text-slate-800">
                      <UserIcon size={20} className="text-primary" />
                      {techName}
                    </h4>
                  </div>

                  {/* Calculations - Simplified */}
                  <div className="space-y-4 bg-white rounded-lg p-4 border border-slate-200">
                    {/* Total general del tecnico */}
                    <div className="flex justify-between items-center pb-3 border-b border-slate-200">
                      <span className="text-sm font-medium text-emerald-700">{"Total General del Técnico"}</span>
                      <span className="text-sm font-medium text-emerald-600">${formatCurrency(totalGeneral)}</span>
                    </div>

                    {/* Loans breakdown */}
                    <div className="pb-3 border-b-2 border-amber-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-amber-700">{"(-) Préstamos Pendientes"}</span>
                        <span className="text-sm font-medium text-amber-700">${formatCurrency(loansTotal)}</span>
                      </div>
                      <div className="ml-4 space-y-1">
                        {techLoans.map(loan => (
                          <div key={loan.id} className="flex justify-between items-center text-sm">
                            <span className="text-slate-500 truncate max-w-xs">- {loan.description}</span>
                            <span className="text-slate-600 ml-2">${formatCurrency(loan.amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Final total to pay */}
                    <div className="bg-primary/5 -mx-4 px-4 py-4 -mb-4 rounded-b-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-primary">TOTAL A PAGAR FINAL</span>
                        <span className="text-sm font-semibold text-primary">${formatCurrency(netTotal)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Show message if no technicians have loans */}
            {!serverTechnicianGroups.some(group => loans.some(loan => loan.technicianName === group.technician)) && (
              <div className="text-center py-8 text-slate-500">
                <p className="text-sm">{"No hay préstamos pendientes para los técnicos con casos a facturar"}</p>
              </div>
            )}
          </div>
        </div>
      )}
      </div>

      {/* Create Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-scale-in">
            <div className="p-6 flex justify-between items-center border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 text-primary rounded-xl">
                  <FileText size={20} />
                </div>
                <h3 className="text-sm font-medium text-slate-800">Nuevo Expediente</h3>
              </div>
              <button onClick={() => setIsCreateModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Fecha</label>
                  <input
                    type="date"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    value={manualInvoice.date}
                    onChange={(e) => setManualInvoice({ ...manualInvoice, date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{"Número de expediente *"}</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Ej: 123456"
                    value={manualInvoice.id}
                    onChange={(e) => setManualInvoice({ ...manualInvoice, id: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{"Técnico / Usuario"}</label>
                <select
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary mb-2"
                  value={manualInvoice.technicianId}
                  onChange={(e) => {
                    const selected = users.find(u => u.id === e.target.value);
                    setManualInvoice({
                      ...manualInvoice,
                      technicianId: e.target.value,
                      technicianName: selected ? selected.name : ''
                    });
                  }}
                >
                  <option value="">-- Seleccionar usuario del sistema --</option>
                  {activeUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
                {!manualInvoice.technicianId && (
                  <input
                    type="text"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="O escriba nombre externo..."
                    value={manualInvoice.technicianName}
                    onChange={(e) => setManualInvoice({ ...manualInvoice, technicianName: e.target.value })}
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Aseguradora</label>
                  <select
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    value={manualInvoice.insurer}
                    onChange={(e) => setManualInvoice({ ...manualInvoice, insurer: e.target.value })}
                  >
                    <option value="ASSA">ASSA</option>
                    <option value={'Óptima de Seguros'}>{'Óptima de Seguros'}</option>
                    <option value="La Regional de Seguros">La Regional de Seguros</option>
                    <option value="Seguros Fedpa">Seguros Fedpa</option>
                    <option value="Seguros Global">Seguros Global</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Servicio *</label>
                  <select
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    value={manualInvoice.service}
                    onChange={(e) => setManualInvoice({ ...manualInvoice, service: e.target.value })}
                  >
                    <option value="">Seleccione un servicio</option>
                    {MANUAL_SERVICE_OPTIONS.map(service => <option key={service} value={service}>{service}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Monto ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    value={manualInvoice.amount}
                    onChange={(e) => setManualInvoice({ ...manualInvoice, amount: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Recargo ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    value={manualInvoice.surcharge}
                    onChange={(e) => setManualInvoice({ ...manualInvoice, surcharge: e.target.value })}
                  />
                </div>
              </div>


            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="px-6 py-2.5 text-slate-600 font-medium hover:bg-slate-200 rounded-xl transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateManualSubmit}
                className="px-8 py-2.5 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover shadow-lg shadow-primary/25 transition-all"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in">
            <div className="p-6 flex justify-between items-center border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 text-emerald-600 rounded-xl">
                  <FileSpreadsheet size={20} />
                </div>
                <h3 className="text-sm font-medium text-slate-800">Importar Expedientes</h3>
              </div>
              <button onClick={() => { setIsUploadModalOpen(false); setImportFeedback(null); setSelectedFile(null); }} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            {importFeedback && (
              <div
                className={`mx-6 mt-4 rounded-2xl border px-4 py-3 shadow-sm ${
                  importFeedback.type === 'warning'
                    ? 'border-amber-200 bg-amber-50 text-amber-800'
                    : importFeedback.type === 'success'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                    : 'border-red-200 bg-red-50 text-red-800'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold">{importFeedback.title}</p>
                    <p className="text-sm leading-6">{importFeedback.message}</p>
                    {renderImportFeedbackDetails()}
                  </div>
                  <button
                    type="button"
                    onClick={() => setImportFeedback(null)}
                    className="rounded-lg p-1 transition hover:bg-black/5"
                    aria-label="Cerrar aviso"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            )}

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Aseguradora (predeterminada)</label>
                <select
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  value={uploadInsurer}
                  onChange={(e) => setUploadInsurer(e.target.value)}
                >
                  <option value="ASSA">ASSA</option>
                  <option value="La Regional de Seguros">La Regional de Seguros</option>
                  <option value="Seguros Fedpa">Seguros Fedpa</option>
                  <option value="Seguros Global">Seguros Global</option>
                </select>
                <p className="text-xs text-slate-500 mt-2">{'Óptima de Seguros se registra manualmente.'}</p>
              </div>

              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${selectedFile ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 hover:border-primary'}`}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                />
                {selectedFile ? (
                  <div className="text-emerald-600">
                    <FileSpreadsheet size={40} className="mx-auto mb-2" />
                    <p className="font-medium">{selectedFile.name}</p>
                    <p className="text-xs text-slate-500 mt-1">Clic para cambiar archivo</p>
                  </div>
                ) : (
                  <div className="text-slate-400">
                    <Upload size={40} className="mx-auto mb-2" />
                    <p className="font-medium">Arrastra o haz clic para seleccionar</p>
                    <p className="text-xs mt-1">Excel (.xlsx, .xls) o CSV</p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
              <button
                onClick={() => { setIsUploadModalOpen(false); setSelectedFile(null); setImportFeedback(null); }}
                className="px-6 py-2.5 text-slate-600 font-medium hover:bg-slate-200 rounded-xl transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleProcessFile}
                disabled={!selectedFile || isProcessing}
                className="flex items-center gap-2 px-8 py-2.5 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isProcessing ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    Procesar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Date Modal */}
      {isBulkDateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl animate-scale-in">
            <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-indigo-50 to-white p-6">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-indigo-100 p-2 text-indigo-600">
                  <Calendar size={20} />
                </div>
                <h3 className="text-sm font-medium text-slate-800">Cambiar fecha en lote</h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsBulkDateModalOpen(false);
                  setBulkDateValue('');
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4 p-6">
              <p className="text-sm text-slate-600">
                Asigna una misma fecha a <span className="font-semibold text-slate-800">{selectedCount}</span> caso{selectedCount !== 1 ? 's' : ''} seleccionado{selectedCount !== 1 ? 's' : ''} en la pestaña actual.
              </p>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Nueva fecha</label>
                <input
                  type="date"
                  value={bulkDateValue}
                  onChange={(e) => setBulkDateValue(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-slate-100 bg-slate-50 p-6">
              <button
                type="button"
                onClick={() => {
                  setIsBulkDateModalOpen(false);
                  setBulkDateValue('');
                }}
                className="rounded-xl px-6 py-2.5 font-medium text-slate-600 transition-colors hover:bg-slate-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleBulkDateUpdate}
                className="rounded-xl bg-indigo-600 px-8 py-2.5 font-bold text-white shadow-lg transition-all hover:bg-indigo-700"
              >
                Guardar fecha
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Objection Modal */}
      {isObjectionModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white shadow-2xl animate-scale-in">
            <div className="p-6 flex justify-between items-center border-b border-slate-100 bg-gradient-to-r from-amber-50 to-white">
              <h3 className="text-sm font-medium text-slate-800">Registrar objeción</h3>
              <button onClick={resetObjectionModalState} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {pendingObjectionCharges.length > 0 && (
                <div className="space-y-2 rounded-2xl border border-amber-200 bg-amber-50 p-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-amber-800">
                    Objeciones agregadas ({pendingObjectionCharges.length})
                  </p>
                  {pendingObjectionCharges.map((entry) => (
                    <div key={entry.clientId} className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2 text-sm">
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-slate-800">{entry.charge.label}</p>
                        <p className="truncate text-xs text-slate-500">
                          {entry.charge.type === 'price_modification'
                            ? `Precio: $${formatCurrency(entry.charge.informationalPrice || 0)}`
                            : `Recargo: $${formatCurrency(entry.charge.amount || 0)}`}
                          {entry.file ? ` · ${entry.file.name}` : ''}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPendingObjectionCharges((current) => current.filter((item) => item.clientId !== entry.clientId))}
                        className="rounded-lg p-2 text-red-500 hover:bg-red-50"
                        aria-label={`Quitar ${entry.charge.label}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Motivo</label>
                  <select
                    value={objectionChargeType}
                    onChange={(e) => updateObjectionChargeType(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Selecciona un motivo</option>
                    <option value="night_guard">Guardia nocturna</option>
                    <option value="mileage">Kilometraje</option>
                    <option value="toll">Peaje</option>
                    <option value="price_modification">Modificación de precio</option>
                  </select>
                </div>

                {objectionChargeType === 'night_guard' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-slate-600">Costo del servicio ($)</label>
                      <input
                        type="number"
                        readOnly
                        value={objectionAmount}
                        className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2.5 text-sm text-slate-700 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-slate-600">Recargo automático ($)</label>
                      <input
                        type="number"
                        readOnly
                        value={objectionSurcharge}
                        className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2.5 text-sm text-slate-700 focus:outline-none"
                      />
                    </div>
                  </div>
                )}

                {objectionChargeType === 'mileage' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Km recorridos *</label>
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        required
                        aria-required="true"
                        value={objectionKilometers}
                        onChange={(e) => updateObjectionMileage('kilometers', e.target.value)}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Precio por km ($)</label>
                      <input
                        type="number"
                        readOnly
                        value={objectionKmPrice}
                        placeholder="Sin configurar"
                        className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2.5 text-sm text-slate-700 focus:outline-none"
                      />
                    </div>
                    <p className="col-span-2 text-xs text-slate-500">La foto es obligatoria para kilometraje.</p>
                  </div>
                )}

                {objectionChargeType === 'toll' && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Precio de caseta ($) *</label>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      required
                      aria-required="true"
                      value={objectionTollPrice}
                      onChange={(e) => updateObjectionTollPrice(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <p className="mt-2 text-xs text-slate-500">La foto es obligatoria para peaje.</p>
                  </div>
                )}

                {objectionChargeType === 'price_modification' && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Precio ($) *</label>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      required
                      aria-required="true"
                      value={objectionInformationalPrice}
                      onChange={(e) => setObjectionInformationalPrice(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                )}
              </div>

              {objectionChargeType && objectionChargeType !== 'price_modification' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Comentario del reclamo</label>
                <textarea
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary min-h-[100px]"
                  placeholder="Describa el motivo..."
                  value={objectionReason}
                  onChange={(e) => setObjectionReason(e.target.value)}
                />
              </div>
              )}

              {(objectionChargeType === 'mileage' || objectionChargeType === 'toll') && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  {objectionChargeType === 'mileage' ? 'Foto del mapa *' : 'Foto de Panapass *'}
                </label>
                <input
                  ref={objectionAttachmentInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const nextFile = e.target.files?.[0] || null;
                    if (!nextFile) {
                      setObjectionAttachmentFile(null);
                      return;
                    }
                    if (!nextFile.type?.startsWith('image/')) {
                      addNotification('Adjunto inválido', 'Solo se permiten imágenes.', 'warning');
                      e.target.value = '';
                      setObjectionAttachmentFile(null);
                      return;
                    }
                    if (nextFile.size > 10 * 1024 * 1024) {
                      addNotification('Adjunto inválido', 'La imagen no puede superar 10 MB.', 'warning');
                      e.target.value = '';
                      setObjectionAttachmentFile(null);
                      return;
                    }
                    setObjectionAttachmentFile(nextFile);
                  }}
                />
                <button
                  type="button"
                  onClick={() => objectionAttachmentInputRef.current?.click()}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  <ImagePlus size={16} />
                  {objectionAttachmentFile ? 'Cambiar foto' : 'Adjuntar foto'}
                </button>
                {objectionAttachmentFile && (
                  <div className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                    <span className="truncate">{objectionAttachmentFile.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setObjectionAttachmentFile(null);
                        if (objectionAttachmentInputRef.current) {
                          objectionAttachmentInputRef.current.value = '';
                        }
                      }}
                      className="rounded-lg px-2 py-1 text-slate-500 transition hover:bg-slate-200 hover:text-slate-700"
                    >
                      Quitar
                    </button>
                  </div>
                )}
                <p className="text-xs text-slate-500">
                  El adjunto se elimina automáticamente después de 2 meses.
                </p>
              </div>
              )}
              {objectionChargeType && (
                <button
                  type="button"
                  onClick={handleAddObjectionCharge}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-bold text-amber-800 transition hover:bg-amber-100"
                >
                  <Plus size={16} />
                  Agregar otra objeción
                </button>
              )}
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
              <button
                onClick={resetObjectionModalState}
                className="px-6 py-2.5 text-slate-600 font-medium hover:bg-slate-200 rounded-xl transition-colors"
              >
                Cancelar
              </button>
              {(objectionChargeType || pendingObjectionCharges.length > 0) && (
              <button
                onClick={handleConfirmObjection}
                disabled={isUploadingObjectionAttachment}
                className="px-8 py-2.5 bg-amber-500 text-white font-bold rounded-xl hover:bg-amber-600 shadow-lg transition-all disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isUploadingObjectionAttachment
                  ? 'Subiendo adjuntos...'
                  : `Enviar objeciones${pendingObjectionCharges.length ? ` (${pendingObjectionCharges.length + (objectionChargeType ? 1 : 0)})` : ''}`}
              </button>
              )}
            </div>
          </div>
        </div>
      )}
      {isCommentsModalOpen && selectedCommentInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl animate-scale-in">
            <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white p-6">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-primary/10 p-2 text-primary">
                  <MessageSquare size={20} />
                </div>
                <div>
                  <h3 className="font-heading text-base font-bold text-slate-800">Comentarios del reclamo</h3>
                  <p className="text-sm text-slate-500">
                    Expediente {selectedCommentInvoice.expediente || selectedCommentInvoice.id}
                  </p>
                </div>
              </div>
              <button onClick={closeCommentsModal} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6 p-6">
              {selectedCommentInvoice?.objectionAttachment && (
                <div className="flex items-center justify-between rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                  <div className="flex items-center gap-2 text-sm text-amber-800">
                    <Paperclip size={16} />
                    <span className="truncate font-medium">
                      {selectedCommentInvoice.objectionAttachment.originalName || 'Adjunto de objeción'}
                    </span>
                  </div>
                  <a
                    href={`${API_URL}/api/invoices/${selectedCommentInvoice.id}/objection-attachment`}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-lg border border-amber-300 px-3 py-1.5 text-xs font-semibold text-amber-800 transition hover:bg-amber-100"
                  >
                    Ver adjunto
                  </a>
                </div>
              )}

              {Array.isArray(selectedCommentInvoice?.objectionCharges) && selectedCommentInvoice.objectionCharges.length > 0 && (
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                    <h4 className="text-sm font-bold text-slate-800">Motivos y recargos del reclamo</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-white text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-4 py-3">Motivo</th>
                          <th className="px-4 py-3">Detalle</th>
                          <th className="px-4 py-3">Evidencia</th>
                          <th className="px-4 py-3 text-right">Recargo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {selectedCommentInvoice.objectionCharges.map((charge, index) => {
                          const detail = charge.type === 'mileage'
                            ? `${charge.kilometers || 0} km x $${formatCurrency(charge.pricePerKm || 0)}`
                            : charge.type === 'toll'
                              ? `Caseta: $${formatCurrency(charge.tollPrice || charge.amount || 0)}`
                              : charge.type === 'price_modification'
                                ? `Precio: $${formatCurrency(charge.informationalPrice || 0)}`
                                : 'Recargo fijo';

                          return (
                            <tr key={`${charge.type || 'charge'}-${index}`}>
                              <td className="px-4 py-3 font-medium text-slate-800">
                                {charge.label || formatObjectionChargeType(charge.type)}
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                <p>{detail}</p>
                                {charge.comment && <p className="mt-1 text-xs text-slate-500">{charge.comment}</p>}
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {charge.attachment && charge.id ? (
                                  <a
                                    href={`${API_URL}/api/invoices/${selectedCommentInvoice.id}/objection-charges/${charge.id}/attachment`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                                  >
                                    <Paperclip size={13} /> Ver foto
                                  </a>
                                ) : '—'}
                              </td>
                              <td className="px-4 py-3 text-right font-semibold text-slate-900">
                                {charge.type === 'price_modification' ? '—' : `$${formatCurrency(charge.amount || 0)}`}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex justify-start">
                  <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-white p-4 shadow-sm">
                    <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-blue-600">
                      <UserIcon size={14} />
                      Técnico
                    </div>
                    <textarea
                      value={userCommentDraft}
                      onChange={(e) => setUserCommentDraft(e.target.value)}
                      readOnly={canManageInvoices}
                      className={`min-h-[120px] w-full resize-none bg-transparent text-sm text-slate-700 focus:outline-none ${canManageInvoices ? 'cursor-default' : ''}`}
                      placeholder="Escribe aquí el motivo del reclamo o cualquier comentario adicional."
                    />
                    {(selectedCommentInvoice.claimTechnicianCommentDate || selectedCommentInvoice.claimDate) && (
                      <p className="mt-2 text-xs text-slate-400">
                        {"Último comentario:"} {formatDateDisplay(selectedCommentInvoice.claimTechnicianCommentDate || selectedCommentInvoice.claimDate)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-md bg-primary p-4 text-white shadow-sm">
                    <div className="mb-2 flex items-center justify-end gap-2 text-xs font-semibold uppercase tracking-wide text-white/80">
                      Administrador
                    </div>
                    <textarea
                      value={adminCommentDraft}
                      onChange={(e) => setAdminCommentDraft(e.target.value)}
                      readOnly={!canManageInvoices}
                      className={`min-h-[120px] w-full resize-none bg-transparent text-sm text-white placeholder:text-white/70 focus:outline-none ${!canManageInvoices ? 'cursor-default' : ''}`}
                      placeholder={canManageInvoices ? 'Escribe aquí la respuesta o decisión del reclamo.' : 'Aún no hay respuesta del administrador.'}
                    />
                    {selectedCommentInvoice.claimAdminCommentDate && (
                      <p className="mt-2 text-right text-xs text-white/70">{"Última respuesta:"} {formatDateDisplay(selectedCommentInvoice.claimAdminCommentDate)}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-slate-100 bg-slate-50 p-6">
              <button
                type="button"
                onClick={closeCommentsModal}
                className="rounded-xl px-6 py-2.5 font-medium text-slate-600 transition-colors hover:bg-slate-200"
              >
                Cerrar
              </button>
              {((!canManageInvoices && selectedCommentInvoice.status === 'reclamado') || canManageInvoices) && (
                <button
                  type="button"
                  onClick={handleSaveComments}
                  className="rounded-xl bg-primary px-8 py-2.5 font-bold text-white shadow-lg shadow-primary/25 transition-all hover:bg-primary-hover"
                >
                  Guardar comentarios
                </button>
              )}
            </div>
          </div>
        </div>
      )}

     <ConfirmDialog
      open={confirmState.open}
      title={confirmState.title}
      message={confirmState.message}
      confirmLabel={confirmState.confirmLabel}
      confirmClassName={confirmState.confirmClassName}
      loading={confirmState.loading}
      onCancel={closeConfirmDialog}
      onConfirm={runConfirmAction}
    />
    </div>
  );
};

export default Invoices;

